
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, LineChart, Line,
} from 'recharts';
import {
  TrendingDown, TrendingUp, Users, Download, RefreshCw,
  ChevronDown, ChevronUp, Search, Calendar, Loader2, X, Smartphone, DollarSign, Bell,
  Building2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

// Same API client the BRE Analysis screen uses — it prefixes API_BASE_URL and
// carries the shared axios auth/CSRF setup, so this works on deployed
// environments where a relative fetch('/api/...') would not.
import { adminService } from '@/pagesadmin/adminApi';
import { STAGE_MAP } from './mockData';
import type { JourneyUser, StageCount, DateFilter, BRERejectDetail, PhonePeMISRow, DisbursedUser, DropOffAlertRow, ApprovedPipelineUser } from './types';

// ─── constants ────────────────────────────────────────────────────────────────

const AUTO_REFRESH_MS = 30_000;

// ─── helpers ─────────────────────────────────────────────────────────────────

/**
 * Convert a UTC ISO-8601 string to IST (UTC+5:30) and return a
 * "DD-MM-YYYY HH:MM AM/PM" string suitable for display and CSV export.
 * Works without any external library — just offsets the UTC ms by 5h30m.
 */
const toIST = (utcStr: string | null | undefined, includeSeconds = true): string => {
  if (!utcStr) return '';
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) return utcStr; // pass-through if unparseable
  // Same format as BRE Analysis (Caprion): "28/09/2026, 15:24:00"
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds ? { second: '2-digit' } : {}),
    hour12: false,
  });
};

/** Return today's date string in IST as YYYY-MM-DD. */
const todayIST = (): string => {
  // IST = UTC + 5:30
  const istMs = Date.now() + 5.5 * 60 * 60 * 1000;
  const ist = new Date(istMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}`;
};

/**
 * Return the IST date portion (YYYY-MM-DD) of a UTC ISO string.
 * NOTE: toIST() returns "DD-MM-YYYY HH:MM AM/PM" — we must NOT use .slice(0,10)
 * on that format (it would give "DD-MM-YYYY", not "YYYY-MM-DD").
 */
const dateIST = (utcStr: string): string => {
  if (!utcStr) return '';
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) return '';
  const istMs = d.getTime() + 5.5 * 60 * 60 * 1000;
  const ist = new Date(istMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}`;
};

/**
 * Add `days` (may be negative) to an IST date string (YYYY-MM-DD) and return
 * the result as a YYYY-MM-DD string, always in IST.  Avoids using UTC Date
 * arithmetic which would give the wrong calendar day when the browser/server
 * clock is not in IST.
 */
const offsetISTDate = (istDateStr: string, days: number): string => {
  const [y, m, d] = istDateStr.split('-').map(Number);
  // Construct a Date at noon IST (UTC+5:30) so DST/UTC-midnight edge cases
  // can never shift the calendar day.
  const base = new Date(Date.UTC(y, m - 1, d, 6, 30, 0)); // noon IST = 06:30 UTC
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().split('T')[0]; // still a plain YYYY-MM-DD
};

const downloadCSV = (users: JourneyUser[], filename: string) => {
  // Escape a single cell value: wrap in quotes if it contains comma, quote, or newline.
  const esc = (v: unknown): string => {
    const s = v == null ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const BUCKET_LABEL: Record<string, string> = {
    pre_bre: 'Pre-BRE', bre: 'BRE', post_bre: 'Post-BRE',
  };
  const SUB_BUCKET_LABEL: Record<string, string> = {
    bre1: 'BRE 1', bre2_aa: 'BRE 2 / AA',
  };
  const headers = [
    'ID', 'Name', 'Phone', 'Username',
    'Business Name', 'BAP / Lender',
    'Stage #', 'Stage Name',
    'Bucket', 'Sub-Bucket',
    'Final Stage (Caprion)',
    'BRE Status', 'Application Status',
    'Approved Amount (₹)',
    'Loan Amount (₹)',
    'Amt in Bank (₹)',
    'Daily EDI (₹)',
    'Total Fee (₹)',
    'Repay Total (₹)',
    'Source Slug',
    'Application ID',
    'Order ID / URN',
    'BRE1 Response ID',
    'BRE2 Response ID',
    'Lead Created (IST)',
    'Last Updated',
  ];
  const rows = users.map(u => [
    u.id,
    u.name,
    u.phone,
    u.username,
    u.businessName,
    u.bap,
    u.stage,
    u.stageName,
    BUCKET_LABEL[u.bucket] ?? u.bucket,
    u.subBucket ? (SUB_BUCKET_LABEL[u.subBucket] ?? u.subBucket) : '',
    u.caprion_final_status ?? '',
    u.bre_status ?? '',
    u.application_status ?? '',
    u.approved_loan_amount != null ? u.approved_loan_amount : '',
    u.loan_amount != null ? u.loan_amount : '',
    u.offer_loan_amt_net != null ? u.offer_loan_amt_net : '',
    u.offer_daily_repayment != null ? u.offer_daily_repayment : '',
    u.offer_total_fee != null ? u.offer_total_fee : '',
    u.offer_repay_total != null ? u.offer_repay_total : '',
    u.source_slug ?? '',
    u.application_id ?? '',
    u.order_id ?? '',
    // The list API no longer ships response bodies (they were ~2.7 KB x3 per
    // user), so the CSV carries the call IDs instead. Full bodies remain
    // available per-call via /api/admin/caprion/check_status_response/?id=.
    u.bre1_response_id ?? '',
    u.bre2_response_id ?? '',
    toIST(u.userCreatedAt),
    toIST(u.lastUpdated),
  ].map(esc));
  const csv = [headers.map(esc), ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

function applyDateFilter(
  allUsers: JourneyUser[],
  filter: DateFilter,
  customFrom: string,
  customTo: string,
): JourneyUser[] {
  if (!allUsers.length) return allUsers;
  // All date comparisons are done in IST so "today" / "yesterday" match the
  // Indian calendar day, not the UTC day.
  const sortedDates = [...new Set(allUsers.map(u => dateIST(u.lastUpdated)))].sort();
  const latestDate   = sortedDates[sortedDates.length - 1];
  const previousDate = sortedDates[sortedDates.length - 2];
  if (filter === 'today')     return allUsers.filter(u => dateIST(u.lastUpdated) === latestDate);
  if (filter === 'yesterday') return previousDate ? allUsers.filter(u => dateIST(u.lastUpdated) === previousDate) : [];
  if (filter === 'last7days' || filter === 'last30days') return allUsers;
  if (filter === 'custom' && customFrom && customTo) {
    return allUsers.filter(u => { const d = dateIST(u.lastUpdated); return d >= customFrom && d <= customTo; });
  }
  return allUsers;
}

// ─── Lender ↔ BAP mapping ─────────────────────────────────────────────────────
// Maps the BAP name (as returned by the API) to the lender display name.
// This is the source-of-truth for the filter labels shown in the UI.
const BAP_TO_LENDER: Record<string, string> = {
  PetPooja: 'MAS (PetPooja)',
  PhonePe:  'Caprion (PhonePe)',
};

// Maps lender_code (from disbursed_users) back to the BAP name so we can
// filter disbursed users with the same bap-based filter state.
const LENDER_CODE_TO_BAP: Record<string, string> = {
  INTELLEND: 'PetPooja',
  CAPRION:   'PhonePe',
};

const BUCKET_META = {
  pre_bre:  { label: 'Pre-BRE',  color: '#3b82f6', bg: 'bg-blue-50',   border: 'border-blue-500',   text: 'text-blue-700',   badgeCls: 'bg-blue-100 text-blue-800'   },
  bre:      { label: 'BRE',      color: '#f97316', bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', badgeCls: 'bg-orange-100 text-orange-800' },
  post_bre: { label: 'Post-BRE', color: '#22c55e', bg: 'bg-green-50',  border: 'border-green-500',  text: 'text-green-700',  badgeCls: 'bg-green-100 text-green-800'  },
} as const;

const DISBURSED_COLOR = '#10b981';

const fmtRupeeIN = (n: number) =>
  `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

type BucketKey = keyof typeof BUCKET_META;

// ─── Caprion Final Status badge (mirrors CaprionJourneyStatus.tsx statusClass) ─

const FinalStatusBadge: React.FC<{
  status: string | null | undefined;
  onClick?: () => void;
}> = ({ status, onClick }) => {
  const base = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold';
  const clickable = onClick ? ' cursor-pointer hover:opacity-80 transition-opacity' : '';
  const wrap = (cls: string, label: string) =>
    onClick
      ? <button className={`${base} ${cls}${clickable}`} onClick={onClick} title="Click to view JSON response">{label}</button>
      : <span className={`${base} ${cls}`}>{label}</span>;

  if (!status) return <span className={`${base} bg-slate-100 text-slate-500`}>—</span>;
  switch (status) {
    case 'APPROVED':        return wrap('bg-green-100 text-green-800', 'APPROVED');
    case 'REJECTED':        return wrap('bg-red-100 text-red-800', 'REJECTED');
    case 'OPS_VERIFICATION':return wrap('bg-blue-100 text-blue-800', 'OPS VERIFY');
    case 'WITHDRAWN':       return wrap('bg-gray-200 text-gray-700', 'WITHDRAWN');
    case 'PENDING':         return wrap('bg-amber-100 text-amber-800', 'PENDING');
    case 'CREATED':         return wrap('bg-slate-100 text-slate-700', 'CREATED');
    default:                return wrap('bg-slate-100 text-slate-700', status);
  }
};

// ─── CaprionJSONViewerModal ───────────────────────────────────────────────────

const CaprionJSONViewerModal: React.FC<{
  user: JourneyUser | null;
  /**
   * CaprionRequestResponse row id whose body to show. The funnel list no longer
   * embeds response bodies (~2.7 KB x3 per user), so the body is fetched here,
   * once, when the modal opens.
   */
  responseId: number | null;
  open: boolean;
  onClose: () => void;
}> = ({ user, responseId, open, onClose }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [json, setJson] = React.useState<Record<string, any> | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Only fetch while actually open, and abort if the operator closes the
    // dialog (or opens a different row) before the response lands.
    if (!open || responseId == null) {
      setJson(null);
      setError(null);
      return;
    }
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(null);
    setJson(null);

    // RELATIVE fetch, matching how this dashboard loads its main list (see the
    // journey_funnel_data fetch below) so it always hits the same origin and
    // carries the session cookie.
    fetch(`/api/admin/caprion/check_status_response/?id=${responseId}`, {
      credentials: 'include',
      cache: 'no-store',
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!active) return;
        if (!res.ok) {
          // 403 is the common, actionable one: this endpoint is superuser-only,
          // so a BAP/LE operator gets told why rather than seeing a blank panel.
          throw new Error(
            res.status === 403
              ? 'Superuser access is required to view the raw response.'
              : `Could not load the response body (HTTP ${res.status}).`,
          );
        }
        const body = await res.json();
        if (!active) return;
        setJson(body.response ?? null);
      })
      .catch((err) => {
        if (!active || err?.name === 'AbortError') return;
        setError(err?.message || 'Could not load the response body.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [open, responseId]);

  if (!user) return null;
  const prettyJson = JSON.stringify(json, null, 2);

  const statusCls = (s: string | null | undefined) => {
    switch (s) {
      case 'APPROVED':         return 'bg-green-100 text-green-800 border-green-200';
      case 'REJECTED':         return 'bg-red-100 text-red-800 border-red-200';
      case 'OPS_VERIFICATION': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PENDING':          return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'CREATED':          return 'bg-slate-100 text-slate-700 border-slate-200';
      default:                 return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      {/*
       * WIDTH CONTAINMENT: same trick as CaprionJourneyStatus.tsx — min-w-0 on
       * every grid level lets the <pre> wrap instead of forcing the dialog wider.
       */}
      <DialogContent className="grid max-h-[85vh] w-[min(56rem,95vw)] max-w-none grid-rows-[auto_minmax(0,1fr)] overflow-hidden">
        <DialogHeader className="min-w-0">
          <DialogTitle className="pr-8 text-base">
            Caprion Check-Status Response
            <span className="ml-2 text-xs font-normal text-gray-500">
              {user.name} · {user.phone}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid min-h-0 min-w-0 grid-rows-[auto_minmax(0,1fr)_auto] gap-3">
          {/* Status badge + user meta */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {user.caprion_final_status && (
              <Badge
                variant="outline"
                className={`font-semibold ${statusCls(user.caprion_final_status)}`}
              >
                {user.caprion_final_status}
              </Badge>
            )}
            <Badge variant="outline">{user.bap}</Badge>
            <Badge variant="outline">Stage {user.stage}: {user.stageName}</Badge>
            {user.loan_amount != null && user.loan_amount > 0 && (
              <Badge variant="outline">₹{user.loan_amount.toLocaleString('en-IN')}</Badge>
            )}
          </div>

          {/*
           * JSON body — lazy-loaded. The list API sends only the call ID, so the
           * body arrives from /api/admin/caprion/check_status_response/ after
           * the dialog opens.
           */}
          {loading ? (
            <div className="flex items-center justify-center gap-2 rounded-lg bg-slate-50 border border-slate-200 py-10 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading response…
            </div>
          ) : error ? (
            <div className="flex items-center justify-center rounded-lg bg-red-50 border border-red-200 py-10 px-4 text-center text-sm text-red-700">
              {error}
            </div>
          ) : json ? (
            <pre className="min-w-0 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
              {prettyJson}
            </pre>
          ) : responseId == null ? (
            // The row carried no call reference at all, so there is nothing to
            // fetch. Said explicitly rather than showing an empty panel.
            <div className="flex items-center justify-center rounded-lg bg-slate-50 border border-slate-200 py-10 px-4 text-center text-sm text-gray-400">
              No Caprion check-status call is recorded against this user yet.
            </div>
          ) : (
            <div className="flex items-center justify-center rounded-lg bg-slate-50 border border-slate-200 py-10 text-sm text-gray-400">
              No check-status response body available for this user.
            </div>
          )}

          {/* Footer actions */}
          <div className="flex justify-end gap-2">
            {json && !loading && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigator.clipboard?.writeText(prettyJson)}
              >
                Copy JSON
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onClose} className="gap-1">
              <X className="h-3 w-3" /> Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── BRE badge ────────────────────────────────────────────────────────────────

const BREBadge: React.FC<{ user: JourneyUser; onClickRejected: (u: JourneyUser) => void }> = ({ user, onClickRejected }) => {
  const s = user.application_status;
  if (s === 'BRE_COMPLETED')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">🟢 Approved</span>;
  if (s === 'BRE_MANUAL_APPROVAL')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">🟡 Conditional</span>;
  if (s === 'BRE_REJECTED')
    return (
      <button
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer transition-colors"
        onClick={() => onClickRejected(user)}
        title="Click to view rejection details"
      >🔴 Rejected</button>
    );
  if (s === 'BRE_ERROR')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">🟠 Error</span>;
  if (s === 'ELIGIBLE_AMOUNT_IS_LESS')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">🟡 Low Amount</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">⚪ Pending</span>;
};

// ─── BRERejectModal ───────────────────────────────────────────────────────────

const BRERejectModal: React.FC<{ user: JourneyUser | null; open: boolean; onClose: () => void }> = ({ user, open, onClose }) => {
  const [detail, setDetail]   = useState<BRERejectDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user) { setDetail(null); setError(null); return; }
    setLoading(true); setDetail(null); setError(null);
    fetch(`/api/admin/bre_reject_detail/?user_id=${user.id}`, { credentials: 'include' })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then((data: BRERejectDetail) => setDetail(data))
      .catch(err => setError(`Failed to load BRE details: ${err.message}`))
      .finally(() => setLoading(false));
  }, [open, user]);

  if (!user) return null;
  const fmt = (n: number) => n ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="text-red-600">🔴</span> BRE Rejection Details
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-start justify-between border-b pb-3 mb-3">
          <div>
            <p className="font-semibold text-gray-900">{user.name}</p>
            <p className="text-sm text-gray-500 font-mono">{user.phone}</p>
            <p className="text-xs text-gray-400 mt-0.5">{user.businessName}</p>
          </div>
          <Badge variant="outline" className="text-xs shrink-0">{user.bap}</Badge>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-8 gap-2 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading BRE details…</span>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
        )}
        {!loading && !error && detail && !detail.has_bre_data && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-500 text-center">
            No BRE data available for this user.
          </div>
        )}
        {!loading && !error && detail && detail.has_bre_data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Lending Entity</p>
                <p className="font-semibold text-gray-900 text-sm">{detail.lending_entity || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">BRE Stage</p>
                <p className="font-semibold text-gray-900 text-sm">{detail.bre_stage || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Requested Amount</p>
                <p className="font-semibold text-gray-900 text-sm">{fmt(detail.requested_amount)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Eligible Amount</p>
                <p className={`font-semibold text-sm ${detail.eligible_amount > 0 ? 'text-green-700' : 'text-red-600'}`}>
                  {fmt(detail.eligible_amount)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Status:</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {detail.application_status || '—'}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Rejection Reasons</p>
              {detail.rejection_reasons.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No specific reasons recorded.</p>
              ) : (
                <ul className="space-y-1.5">
                  {detail.rejection_reasons.map((reason, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                      <span className="text-red-400 mt-0.5 shrink-0">✕</span>
                      {reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2 border-t mt-2">
          <Button size="sm" variant="outline" onClick={onClose} className="gap-1">
            <X className="h-3 w-3" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── SummaryCard ──────────────────────────────────────────────────────────────

interface SummaryCardProps {
  title: string; value: number | string; subtitle?: string; trend?: number; borderColor: string; icon: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
}
const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, subtitle, trend, borderColor, icon, onClick, active }) => (
  <Card
    className={`border-l-4 ${borderColor} transition-all duration-150
      ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}
      ${active ? 'ring-2 ring-offset-1 ring-gray-400 shadow-md' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          {onClick && !active && (
            <p className="text-[10px] text-indigo-400 mt-1 font-medium">↗ Click to view users</p>
          )}
          {active && (
            <p className="text-[10px] text-gray-500 mt-1 font-medium">✓ Active</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="text-gray-400">{icon}</div>
          {trend !== undefined && (
            <span className={`flex items-center gap-0.5 text-xs font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {Math.abs(trend)}%
            </span>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

// ─── FunnelTooltip ────────────────────────────────────────────────────────────

const FunnelTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-800">{d.stageNameFull}</p>
      <p className="text-gray-600">Users: <span className="font-bold">{d.count}</span></p>
      <p className="text-gray-600 capitalize">Bucket: {String(d.bucket).replace('_', '-')}</p>
      {d.dropOffRate !== undefined && d.dropOffRate > 0 && (
        <p className="text-red-500">Drop-off: {Number(d.dropOffRate).toFixed(1)}%</p>
      )}
    </div>
  );
};

// ─── StageRow ─────────────────────────────────────────────────────────────────

const StageRow: React.FC<{ sc: StageCount; maxCount: number; onSelect: (sc: StageCount) => void }> = ({ sc, maxCount, onSelect }) => {
  const pct = maxCount > 0 ? (sc.count / maxCount) * 100 : 0;
  const drop = sc.dropOffRate ?? 0;
  const dropColor = drop > 50 ? 'text-red-500' : drop > 20 ? 'text-yellow-500' : 'text-green-600';
  const DropIcon = drop > 20 ? TrendingDown : TrendingUp;
  const barColor = BUCKET_META[sc.bucket as BucketKey]?.color ?? '#6b7280';
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer group transition-colors" onClick={() => onSelect(sc)}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-700 truncate">{sc.stageName}</span>
          <div className="flex items-center gap-2 ml-2 shrink-0">
            {sc.dropOffRate !== undefined && sc.dropOffRate > 0 && (
              <span className={`flex items-center gap-0.5 text-xs font-medium ${dropColor}`}>
                <DropIcon className="h-3 w-3" />{drop.toFixed(0)}%
              </span>
            )}
            <span className="text-lg font-bold text-gray-900">{sc.count}</span>
          </div>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: barColor }} />
        </div>
      </div>
      <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2 text-xs"
        onClick={(e) => { e.stopPropagation(); downloadCSV(sc.users, `stage_${sc.stage}_${sc.stageKey}.csv`); }}>
        <Download className="h-3 w-3 mr-1" />CSV
      </Button>
    </div>
  );
};

// ─── BucketPanel ──────────────────────────────────────────────────────────────

const BucketPanel: React.FC<{
  bucketKey: BucketKey; stages: StageCount[]; expanded: boolean; onToggle: () => void; onSelectStage: (sc: StageCount) => void;
}> = ({ bucketKey, stages, expanded, onToggle, onSelectStage }) => {
  const meta = BUCKET_META[bucketKey];
  const total = stages.reduce((s, sc) => s + sc.count, 0);
  const maxCount = Math.max(...stages.map(sc => sc.count), 1);
  const bre1Stages = stages.filter(sc => sc.subBucket === 'bre1');
  const bre2Stages = stages.filter(sc => sc.subBucket === 'bre2_aa');

  // Total loan amount across all users in this bucket (only for bre/post_bre)
  const totalLoanAmount = (bucketKey === 'bre' || bucketKey === 'post_bre')
    ? stages.flatMap(sc => sc.users).reduce((sum, u) => sum + (u.loan_amount ?? 0), 0)
    : 0;
  const hasLoanAmounts = totalLoanAmount > 0;

  return (
    <Card className={`border-t-4 ${meta.border}`}>
      <CardHeader className={`${meta.bg} cursor-pointer select-none py-3 px-4`} onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className={`text-base font-semibold ${meta.text}`}>{meta.label}</CardTitle>
            <Badge className={`${meta.badgeCls} text-xs font-bold`}>{total}</Badge>
            {hasLoanAmounts && (
              <span className={`text-xs font-medium ${meta.text} opacity-80`}>
                ₹{totalLoanAmount.toLocaleString('en-IN')}
              </span>
            )}
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="p-2 space-y-1">
          {bucketKey === 'bre' ? (
            <>
              {bre1Stages.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 pt-2 pb-1">BRE 1</p>
                  {bre1Stages.map(sc => <StageRow key={sc.stageKey} sc={sc} maxCount={maxCount} onSelect={onSelectStage} />)}
                </div>
              )}
              {bre2Stages.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 pt-2 pb-1">BRE 2 / AA</p>
                  {bre2Stages.map(sc => <StageRow key={sc.stageKey} sc={sc} maxCount={maxCount} onSelect={onSelectStage} />)}
                </div>
              )}
            </>
          ) : (
            stages.map(sc => <StageRow key={sc.stageKey} sc={sc} maxCount={maxCount} onSelect={onSelectStage} />)
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ─── StageModal ───────────────────────────────────────────────────────────────

// ─── DisbursedModal helpers ───────────────────────────────────────────────────

/**
 * Returns the display name for a disbursed user, or null if no real name is
 * available.
 *
 * The backend falls back to Django's `username` field (which stores the phone
 * number) when `first_name + last_name` are both empty. That produces a record
 * where `d.name` IS the phone number — a purely-numeric string. We detect that
 * case here and return null so the UI can show "—" instead of a phone number
 * in the Name column.
 *
 * Also handles the alternative `customer_name` field name that the
 * DisbursedLoansListView API sends (vs `name` from the funnel API).
 */
function disbursedDisplayName(d: { name?: string | null; customer_name?: string | null }): string | null {
  const raw = d.customer_name?.trim() || d.name?.trim() || null;
  if (!raw) return null;
  // A name that is entirely digits (with optional leading/trailing spaces
  // already stripped above) is the phone-as-username backend fallback — treat
  // it as absent so it never appears in the Name column.
  if (/^\d+$/.test(raw)) return null;
  return raw;
}

/**
 * Returns the display phone for a disbursed user, or null if unavailable.
 *
 * Handles both field name shapes:
 *  - `phone`        — funnel API (admin_journey_funnel_data)
 *  - `phone_number` — DisbursedLoansListView API
 */
function disbursedDisplayPhone(d: { phone?: string | null; phone_number?: string | null }): string | null {
  return d.phone_number?.trim() || d.phone?.trim() || null;
}

// ─── DisbursedModal ───────────────────────────────────────────────────────────

const DisbursedModal: React.FC<{
  users: DisbursedUser[];
  open: boolean;
  onClose: () => void;
}> = ({ users, open, onClose }) => {
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base font-semibold text-emerald-700">
              💰 Disbursed Users
              <span className="ml-2 text-sm font-normal text-gray-500">({users.length} record{users.length !== 1 ? 's' : ''})</span>
            </DialogTitle>
          </div>
        </DialogHeader>
        <div className="overflow-auto flex-1 mt-2">
          {users.length === 0 ? (
            <div className="text-center text-gray-400 py-12 text-sm">No disbursals found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-emerald-50/50">
                  <TableHead className="text-xs whitespace-nowrap">Loan ID</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Name</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Phone</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Lender</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Disbursed Amt</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Loan Amt</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Amt in Bank</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Daily EDI</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Total Fee</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Repay Total</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">ROI</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Tenure</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">EMI/EDI</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Date of Disbursal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Sort newest-first: null dates fall to the bottom */}
                {[...users].sort((a, b) => {
                  if (!a.date_of_disbursal && !b.date_of_disbursal) return 0;
                  if (!a.date_of_disbursal) return 1;
                  if (!b.date_of_disbursal) return -1;
                  return new Date(b.date_of_disbursal).getTime() - new Date(a.date_of_disbursal).getTime();
                }).map((d, idx) => (
                  <TableRow key={d.intellend_loan_id || idx} className="hover:bg-emerald-50/30">
                    <TableCell className="font-mono text-xs text-emerald-700 whitespace-nowrap">
                      {d.intellend_loan_id || '—'}
                    </TableCell>
                    <TableCell className="font-medium text-sm whitespace-nowrap">
                      {disbursedDisplayName(d) ?? '—'}
                    </TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {disbursedDisplayPhone(d) ?? '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700">
                        {d.lender_code || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-semibold text-emerald-700 whitespace-nowrap">
                      {d.disbursed_amount != null ? fmtRupeeIN(d.disbursed_amount) : '—'}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {d.loan_amount != null ? fmtRupeeIN(d.loan_amount) : '—'}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {d.offer_loan_amt_net != null ? fmtRupeeIN(d.offer_loan_amt_net) : '—'}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {d.offer_daily_repayment != null ? fmtRupeeIN(d.offer_daily_repayment) : '—'}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {d.offer_total_fee != null ? fmtRupeeIN(d.offer_total_fee) : '—'}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {d.offer_repay_total != null ? fmtRupeeIN(d.offer_repay_total) : '—'}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {d.roi != null ? `${d.roi}%` : '—'}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {d.tenure != null ? `${d.tenure}m` : '—'}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {d.epi != null ? fmtRupeeIN(d.epi) : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                      {d.date_of_disbursal ? toIST(d.date_of_disbursal) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StageModal: React.FC<{
  sc: StageCount | null;
  open: boolean;
  onClose: () => void;
  onViewJson?: (u: JourneyUser) => void;
}> = ({ sc, open, onClose, onViewJson }) => {
  if (!sc) return null;
  const meta = BUCKET_META[sc.bucket as BucketKey];
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              Users at <Badge className={`${meta?.badgeCls} ml-1`}>{sc.stageName}</Badge>
            </DialogTitle>
            <Button size="sm" variant="outline" className="gap-1"
              onClick={() => downloadCSV(sc.users, `stage_${sc.stage}_${sc.stageKey}.csv`)}>
              <Download className="h-3 w-3" />Download CSV
            </Button>
          </div>
        </DialogHeader>
        <div className="overflow-auto flex-1 mt-2">
          {(() => {
            const hasLoanAmount = sc.users.some(u => u.loan_amount != null && u.loan_amount > 0);
            const hasFinalStatus = sc.users.some(u => u.caprion_final_status != null);
            return (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Business Name</TableHead>
                    <TableHead>BAP</TableHead>
                    {hasFinalStatus && <TableHead>Final Stage</TableHead>}
                    {hasLoanAmount && <TableHead>Loan Amount</TableHead>}
                    <TableHead>Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sc.users.map(u => (
                    <TableRow key={u.id}>
                      <TableCell className="font-mono text-xs">{u.id}</TableCell>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="font-mono text-sm">{u.phone}</TableCell>
                      <TableCell className="text-sm text-gray-600" title={u.businessName || ''}>{u.businessName || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{u.bap}</Badge></TableCell>
                      {hasFinalStatus && (
                        <TableCell>
                          <FinalStatusBadge
                            status={u.caprion_final_status}
                            onClick={u.caprion_check_status_id != null && onViewJson ? () => onViewJson(u) : undefined}
                          />
                        </TableCell>
                      )}
                      {hasLoanAmount && (
                        <TableCell className="text-xs font-medium text-gray-700">
                          {u.loan_amount != null && u.loan_amount > 0
                            ? `₹${u.loan_amount.toLocaleString('en-IN')}`
                            : '—'}
                        </TableCell>
                      )}
                      <TableCell className="text-xs text-gray-500">{toIST(u.lastUpdated, true)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── PhonePe MIS BRE status badge ─────────────────────────────────────────────

const MISBREBadge: React.FC<{ status: string; onClickRejected?: () => void }> = ({ status, onClickRejected }) => {
  if (status === 'BRE_COMPLETED')
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">🟢 Approved</span>;
  if (status === 'BRE_MANUAL_APPROVAL')
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">🟡 Conditional</span>;
  if (status === 'BRE_REJECTED')
    return (
      <button
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer transition-colors border border-red-200 hover:border-red-400 active:scale-95"
        onClick={onClickRejected}
        title="Click to view rejection reasons"
      >
        🔴 Rejected ↗
      </button>
    );
  if (status === 'BRE_ERROR')
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">🟠 Error</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">⚪ {status || 'Pending'}</span>;
};

// ─── BRERejectionDetailModal (PhonePe MIS) ────────────────────────────────────

function BRERejectionDetailModal({ row, onClose }: { row: PhonePeMISRow; onClose: () => void }) {
  const [reasons, setReasons] = useState<string[]>(row.rejection_reasons ?? []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (reasons.length === 0) {
      setLoading(true);
      fetch(`/api/admin/bre_reject_detail/?user_id=${row.user_id}`, { credentials: 'include' })
        .then(r => r.json())
        .then((data: { rejection_reasons?: string[] }) => {
          if (data.rejection_reasons?.length) setReasons(data.rejection_reasons);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.user_id]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-red-800">BRE Rejection Details</h3>
            <p className="text-sm text-red-600">{row.bre_stage ?? 'BRE'} stage rejection</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">✕</button>
        </div>
        {/* User info */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Name</p>
              <p className="font-semibold text-gray-800">{row.name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
              <p className="font-semibold text-gray-800">{row.phone}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Business</p>
              <p className="font-semibold text-gray-800">{row.business_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Loan Requested</p>
              <p className="font-semibold text-gray-800">
                {row.loan_amount_requested != null
                  ? `₹${row.loan_amount_requested.toLocaleString('en-IN')}`
                  : '—'}
              </p>
            </div>
          </div>
        </div>
        {/* Rejection reasons */}
        <div className="px-6 py-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Rejection Reasons</p>
          {loading ? (
            <p className="text-sm text-gray-400 animate-pulse">Loading reasons…</p>
          ) : reasons.length > 0 ? (
            <ul className="space-y-2">
              {reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-red-500 mt-0.5 flex-shrink-0">✗</span>
                  <span className="text-gray-700">{r}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 italic">No rejection reasons available in DB</p>
          )}
        </div>
        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PhonePe MIS Tab ──────────────────────────────────────────────────────────

interface PhonePeMISTabProps {
  dateFrom: string;
  dateTo: string;
}

const PhonePeMISTab: React.FC<PhonePeMISTabProps> = ({ dateFrom, dateTo }) => {
  const [rows, setRows]           = useState<PhonePeMISRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [usingMock, setUsingMock] = useState(false);
  const [searchQuery, setSearch]  = useState('');
  const [currentPage, setPage]    = useState(1);
  const [breFilterMode, setBreFilterMode] = useState<'bre1_rejected' | 'bre2_rejected' | null>(null);
  const [selectedMISUser, setSelectedMISUser] = useState<PhonePeMISRow | null>(null);
  const [isMISModalOpen, setIsMISModalOpen]   = useState(false);
  const PAGE_SIZE = 15;

  const fmtRupee = (n: number | null) => (n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—');
  const fmtNum   = (n: number | null) => (n != null ? String(n) : '—');
  const fmtPct   = (n: number | null) => (n != null ? `${n}%` : '—');

  const loadMIS = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `/api/admin/phonepe_mis/?date_from=${dateFrom}&date_to=${dateTo}`,
        { credentials: 'include', cache: 'no-store' },
      );
      if (res.ok) {
        const data = await res.json();
        if (data.rows && Array.isArray(data.rows)) {
          setRows(data.rows as PhonePeMISRow[]);
          setUsingMock(false);
          setLoading(false);
          return;
        }
      }
    } catch {
      // fall through to mock
    }
    setRows([]);
    setUsingMock(false);
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    setPage(1);
    loadMIS();
  }, [loadMIS]);

  const handleDownloadCSV = () => {
    const url = `/api/admin/phonepe_mis/?date_from=${dateFrom}&date_to=${dateTo}&format=csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `phonepe_mis_${dateFrom}_to_${dateTo}.csv`;
    a.click();
  };

  const filtered = useMemo(() => {
    let result = rows;
    // Apply search filter
    const q = searchQuery.toLowerCase();
    if (q) {
      result = result.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.phone.includes(q) ||
        r.business_name.toLowerCase().includes(q),
      );
    }
    // Apply BRE filter mode
    if (breFilterMode === 'bre1_rejected') {
      result = result.filter(r => r.bre_stage === 'BRE1' && r.application_status === 'BRE_REJECTED');
    } else if (breFilterMode === 'bre2_rejected') {
      result = result.filter(r => r.bre_stage === 'BRE2' && r.application_status === 'BRE_REJECTED');
    }
    return result;
  }, [rows, searchQuery, breFilterMode]);

  // Summary stats — computed from all rows (not filtered) so counts are stable
  const totalUsers  = rows.length;
  const breApproved = rows.filter(r => r.application_status === 'BRE_COMPLETED').length;
  const bre1RejectedCount = rows.filter(r => r.bre_stage === 'BRE1' && r.application_status === 'BRE_REJECTED').length;
  const bre2RejectedCount = rows.filter(r => r.bre_stage === 'BRE2' && r.application_status === 'BRE_REJECTED').length;
  const loanAmounts = rows.map(r => r.loan_amount_requested).filter((v): v is number => v != null);
  const avgLoan     = loanAmounts.length > 0
    ? Math.round(loanAmounts.reduce((a, b) => a + b, 0) / loanAmounts.length)
    : null;

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged      = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-4">
      {/* MIS header row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-sm font-semibold text-gray-800">PhonePe MIS Report</p>
            <p className="text-xs text-gray-500">
              {usingMock ? '⚠ Mock data (API unavailable)' : '✓ Live data'} · {dateFrom} → {dateTo}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={handleDownloadCSV}>
            <Download className="h-3 w-3" />Download MIS CSV
          </Button>
          <Button size="sm" variant="ghost" onClick={loadMIS} disabled={loading} title="Refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <SummaryCard
          title="Total PhonePe Users" value={totalUsers}
          subtitle="in date range" borderColor="border-purple-500"
          icon={<Smartphone className="h-5 w-5 text-purple-400" />}
        />
        <SummaryCard
          title="BRE Approved" value={breApproved}
          subtitle={totalUsers > 0 ? `${((breApproved / totalUsers) * 100).toFixed(0)}% of total` : '—'}
          borderColor="border-green-500"
          icon={<TrendingUp className="h-5 w-5 text-green-400" />}
        />

        {/* BRE1 Rejected — clickable filter card */}
        <div
          onClick={() => { setBreFilterMode(v => v === 'bre1_rejected' ? null : 'bre1_rejected'); setPage(1); }}
          className={`cursor-pointer rounded-xl p-4 border-2 transition-all duration-200 hover:shadow-md
            ${breFilterMode === 'bre1_rejected'
              ? 'border-red-500 bg-red-50 ring-2 ring-red-300'
              : 'border-red-200 bg-white hover:border-red-400'}`}
        >
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">BRE1 Rejected</p>
            <span className="text-red-500 text-lg">↘</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{bre1RejectedCount}</p>
          <p className="text-xs text-red-400 mt-1">
            {breFilterMode === 'bre1_rejected' ? '✓ Filtering table' : 'Click to filter'}
          </p>
        </div>

        {/* BRE2 Rejected — clickable filter card */}
        <div
          onClick={() => { setBreFilterMode(v => v === 'bre2_rejected' ? null : 'bre2_rejected'); setPage(1); }}
          className={`cursor-pointer rounded-xl p-4 border-2 transition-all duration-200 hover:shadow-md
            ${breFilterMode === 'bre2_rejected'
              ? 'border-rose-600 bg-rose-50 ring-2 ring-rose-300'
              : 'border-rose-200 bg-white hover:border-rose-400'}`}
        >
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">BRE2 Rejected</p>
            <span className="text-rose-600 text-lg">↘</span>
          </div>
          <p className="text-2xl font-bold text-rose-700">{bre2RejectedCount}</p>
          <p className="text-xs text-rose-400 mt-1">
            {breFilterMode === 'bre2_rejected' ? '✓ Filtering table' : 'Click to filter'}
          </p>
        </div>

        <SummaryCard
          title="Avg Loan Amount"
          value={avgLoan != null ? `₹${Number(avgLoan).toLocaleString('en-IN')}` : '—'}
          subtitle="requested amount" borderColor="border-blue-500"
          icon={<Users className="h-5 w-5 text-blue-400" />}
        />
      </div>

      {/* Active BRE filter banner */}
      {breFilterMode && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <span>🔍 Showing {breFilterMode === 'bre1_rejected' ? 'BRE1' : 'BRE2'} rejected users only</span>
          <button
            onClick={() => { setBreFilterMode(null); setPage(1); }}
            className="ml-auto text-red-500 hover:text-red-700 font-medium"
          >
            ✕ Clear filter
          </button>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Search name / phone / business…"
            value={searchQuery}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Badge variant="outline" className="text-xs font-normal">{filtered.length} users</Badge>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12 gap-2 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading PhonePe MIS data…</span>
        </div>
      )}

      {/* MIS Table */}
      {!loading && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs whitespace-nowrap">Phone</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Name</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Business</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Stage</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">BRE Status</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Loan Amt</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Eligible Amt</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">ROI</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Tenure</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Risk Grade</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Net Disbursal</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Bureau</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Reject Reason</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center text-gray-400 py-8">
                        No PhonePe users found for the selected date range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paged.map(r => (
                      <TableRow key={r.user_id} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-xs whitespace-nowrap">{r.phone}</TableCell>
                        <TableCell className="font-medium text-sm whitespace-nowrap">{r.name}</TableCell>
                        <TableCell className="text-xs text-gray-600 max-w-[140px] truncate">{r.business_name}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.stage}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <MISBREBadge
                            status={r.application_status}
                            onClickRejected={r.application_status === 'BRE_REJECTED'
                              ? () => { setSelectedMISUser(r); setIsMISModalOpen(true); }
                              : undefined}
                          />
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{fmtRupee(r.loan_amount_requested)}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{fmtRupee(r.eligible_amount)}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{fmtPct(r.roi)}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.tenure != null ? `${r.tenure}m` : '—'}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {r.risk_grade != null ? (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold ${
                              r.risk_grade <= 2 ? 'bg-green-100 text-green-800' :
                              r.risk_grade <= 4 ? 'bg-yellow-100 text-yellow-800' :
                              r.risk_grade <= 6 ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>{r.risk_grade}</span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{fmtRupee(r.net_disbursal)}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap max-w-[100px] truncate" title={r.bureau_decision ?? ''}>
                          {r.bureau_decision || '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap max-w-[140px] truncate" title={r.reject_reason ?? r.bureau_reject_reasons ?? ''}>
                          {r.reject_reason || r.bureau_reject_reasons || '—'}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                          {toIST(r.updated_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <p className="text-xs text-gray-500">Page {currentPage} of {totalPages} · {filtered.length} users</p>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-7 px-2"
                    disabled={currentPage === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                    ‹ Prev
                  </Button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i;
                    return (
                      <Button key={page} size="sm"
                        variant={page === currentPage ? 'default' : 'outline'}
                        className={`h-7 w-7 p-0 text-xs ${page === currentPage ? 'bg-gray-900 text-white' : ''}`}
                        onClick={() => setPage(page)}>
                        {page}
                      </Button>
                    );
                  })}
                  <Button size="sm" variant="outline" className="h-7 px-2"
                    disabled={currentPage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                    Next ›
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* BRE Rejection Detail Modal */}
      {isMISModalOpen && selectedMISUser && (
        <BRERejectionDetailModal
          row={selectedMISUser}
          onClose={() => { setIsMISModalOpen(false); setSelectedMISUser(null); }}
        />
      )}
    </div>
  );
};

// ─── DropOffAlertsTab ─────────────────────────────────────────────────────────

const ALERTS_REFRESH_MS = 60_000;

const ACTION_OPTIONS = [
  { value: '', label: 'Pending' },
  { value: 'called', label: 'Called' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'no_response', label: 'No Response' },
  { value: 'resumed', label: 'Journey Resumed' },
  { value: 'other', label: 'Other' },
];

function fmtStalledFor(minutesStalled: number): string {
  if (minutesStalled < 60) return `${minutesStalled}m ago`;
  const h = Math.floor(minutesStalled / 60);
  const m = minutesStalled % 60;
  return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`;
}

const DropOffAlertsTab: React.FC = () => {
  const [alerts, setAlerts]           = useState<DropOffAlertRow[]>([]);
  const [loading, setLoading]         = useState(true);
  const [usingMock, setUsingMock]     = useState(false);
  const [filter, setFilter]           = useState<'all' | 'urgent' | 'standard' | 'resolved'>('all');
  const [pendingCount, setPendingCount] = useState(0);
  const [urgentCount, setUrgentCount]   = useState(0);
  const [toast, setToast]             = useState<{ msg: string; ok: boolean } | null>(null);

  // Per-row action state: { [alertId]: { action_taken, action_notes, saving } }
  const [rowActions, setRowActions] = useState<Record<number, { action_taken: string; action_notes: string; saving: boolean }>>({});

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAlerts = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/drop_off_alerts/?resolved=false', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.alerts)) {
          // Also fetch resolved for "Resolved Today" count
          const resResolved = await fetch('/api/admin/drop_off_alerts/?resolved=true', { credentials: 'include' }).catch(() => null);
          let resolvedAlerts: DropOffAlertRow[] = [];
          if (resResolved?.ok) {
            const rd = await resResolved.json();
            if (Array.isArray(rd.alerts)) resolvedAlerts = rd.alerts;
          }
          const allAlerts = [...data.alerts, ...resolvedAlerts];
          setAlerts(allAlerts);
          setPendingCount(data.pending_count ?? data.alerts.length);
          setUrgentCount(data.urgent_count ?? 0);
          setUsingMock(false);
          setLoading(false);
          return;
        }
      }
    } catch {
      // fall through to mock
    }
    setAlerts([]);
    setPendingCount(0);
    setUrgentCount(0);
    setUsingMock(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, ALERTS_REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  // Initialise row action state when alerts load
  useEffect(() => {
    const init: Record<number, { action_taken: string; action_notes: string; saving: boolean }> = {};
    alerts.forEach(a => {
      if (!rowActions[a.id]) {
        init[a.id] = { action_taken: a.action_taken, action_notes: a.action_notes, saving: false };
      }
    });
    if (Object.keys(init).length > 0) {
      setRowActions(prev => ({ ...init, ...prev }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts]);

  const filtered = useMemo(() => {
    if (filter === 'urgent')   return alerts.filter(a => !a.resolved && a.tat_zone === 'post_bre');
    if (filter === 'standard') return alerts.filter(a => !a.resolved && a.tat_zone === 'pre_bre');
    if (filter === 'resolved') return alerts.filter(a => a.resolved);
    return alerts; // 'all'
  }, [alerts, filter]);

  const resolvedToday = useMemo(() => {
    const today = todayIST();
    return alerts.filter(a => a.resolved && a.action_logged_at && dateIST(a.action_logged_at) === today).length;
  }, [alerts]);

  const handleSaveAction = async (alertId: number, resolvedFlag: boolean) => {
    const row = rowActions[alertId];
    if (!row) return;
    setRowActions(prev => ({ ...prev, [alertId]: { ...prev[alertId], saving: true } }));

    try {
      const res = await fetch(`/api/admin/drop_off_alerts/${alertId}/action/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_taken: row.action_taken, action_notes: row.action_notes, resolved: resolvedFlag }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated: DropOffAlertRow = await res.json();
      setAlerts(prev => prev.map(a => a.id === alertId ? updated : a));
      setRowActions(prev => ({ ...prev, [alertId]: { action_taken: updated.action_taken, action_notes: updated.action_notes, saving: false } }));
      showToast('Action saved successfully', true);
    } catch (err) {
      setRowActions(prev => ({ ...prev, [alertId]: { ...prev[alertId], saving: false } }));
      showToast(`Failed to save action: ${err instanceof Error ? err.message : 'Unknown error'}`, false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
          toast.ok ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.ok ? '✓' : '✕'} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-red-500" />
          <div>
            <p className="text-sm font-semibold text-gray-800">Drop-off Alerts</p>
            <p className="text-xs text-gray-500">
              {usingMock ? '⚠ Mock data (API unavailable)' : '✓ Live data'} · auto-refreshes every 60s
            </p>
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={loadAlerts} disabled={loading} title="Refresh">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard
          title="Total Pending" value={pendingCount}
          subtitle="unresolved alerts" borderColor="border-gray-400"
          icon={<Bell className="h-5 w-5 text-gray-400" />}
        />
        <SummaryCard
          title="🚨 Urgent (Post-BRE)" value={urgentCount}
          subtitle="5-min TAT breached" borderColor="border-red-500"
          icon={<Bell className="h-5 w-5 text-red-400" />}
        />
        <SummaryCard
          title="⚠ Standard (Pre-BRE)" value={pendingCount - urgentCount}
          subtitle="30-min TAT breached" borderColor="border-yellow-500"
          icon={<Bell className="h-5 w-5 text-yellow-400" />}
        />
        <SummaryCard
          title="Resolved Today" value={resolvedToday}
          subtitle="actions logged today" borderColor="border-green-500"
          icon={<TrendingUp className="h-5 w-5 text-green-400" />}
        />
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'urgent', 'standard', 'resolved'] as const).map(f => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? 'default' : 'outline'}
            className={`text-xs capitalize ${filter === f ? 'bg-gray-900 text-white' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'urgent' ? '🚨 Urgent' : f === 'standard' ? '⚠ Standard' : f === 'resolved' ? '✓ Resolved' : 'All'}
          </Button>
        ))}
        <Badge variant="outline" className="text-xs font-normal ml-auto">{filtered.length} alerts</Badge>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 gap-2 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading alerts…</span>
        </div>
      )}

      {/* Alerts table */}
      {!loading && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs whitespace-nowrap">User</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Phone</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Stage</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Zone</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Stalled For</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Alert Sent</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">SMS</TableHead>
                    <TableHead className="text-xs whitespace-nowrap min-w-[140px]">Action</TableHead>
                    <TableHead className="text-xs whitespace-nowrap min-w-[180px]">Notes</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Mark Resolved</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-gray-400 py-8">
                        No alerts found for the selected filter.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map(alert => {
                      const isUrgent = alert.tat_zone === 'post_bre' && !alert.resolved;
                      const isStandard = alert.tat_zone === 'pre_bre' && !alert.resolved;
                      const rowCls = isUrgent
                        ? 'bg-red-50 hover:bg-red-100'
                        : isStandard
                        ? 'bg-yellow-50 hover:bg-yellow-100'
                        : 'hover:bg-gray-50';
                      const row = rowActions[alert.id] ?? { action_taken: alert.action_taken, action_notes: alert.action_notes, saving: false };

                      return (
                        <TableRow key={alert.id} className={rowCls}>
                          <TableCell className="font-medium text-sm whitespace-nowrap">
                            {alert.name || `User #${alert.user_id}`}
                          </TableCell>
                          <TableCell className="font-mono text-xs whitespace-nowrap">{alert.phone || '—'}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap font-medium">{alert.stage}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {alert.tat_zone === 'post_bre' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">🚨 Post-BRE</span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">⚠ Pre-BRE</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap font-medium">
                            {fmtStalledFor(alert.minutes_stalled)}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                            {toIST(alert.alerted_at)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {alert.sms_sent
                              ? <span className="text-green-600 text-xs font-medium">✓ Sent</span>
                              : <span className="text-gray-400 text-xs">✕ Not sent</span>}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {alert.resolved ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {ACTION_OPTIONS.find(o => o.value === alert.action_taken)?.label || alert.action_taken || 'Resolved'}
                              </span>
                            ) : (
                              <div className="flex items-center gap-1">
                                <select
                                  className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white"
                                  value={row.action_taken}
                                  onChange={e => setRowActions(prev => ({ ...prev, [alert.id]: { ...prev[alert.id], action_taken: e.target.value } }))}
                                >
                                  {ACTION_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-xs"
                                  disabled={row.saving}
                                  onClick={() => handleSaveAction(alert.id, false)}
                                >
                                  {row.saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                                </Button>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {alert.resolved ? (
                              <span className="text-xs text-gray-500 italic max-w-[180px] block truncate" title={alert.action_notes}>
                                {alert.action_notes || '—'}
                              </span>
                            ) : (
                              <Input
                                className="h-6 text-xs min-w-[160px]"
                                placeholder="Add notes…"
                                value={row.action_notes}
                                onChange={e => setRowActions(prev => ({ ...prev, [alert.id]: { ...prev[alert.id], action_notes: e.target.value } }))}
                              />
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {alert.resolved ? (
                              <span className="text-green-600 text-xs font-medium">✓ Resolved</span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-xs border-green-300 text-green-700 hover:bg-green-50"
                                disabled={row.saving}
                                onClick={() => handleSaveAction(alert.id, true)}
                              >
                                {row.saving ? <Loader2 className="h-3 w-3 animate-spin" /> : '✓ Resolve'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── Analytics Panel Types ────────────────────────────────────────────────────

interface StageBreakdownRow { stage: number; name: string; count: number; pct: number; }

interface PreBREAnalytics {
  total: number;
  completed: number;
  dropOff: number;
  conversionRate: number;
  avgStage: number;
  stageBreakdown: StageBreakdownRow[];
}

interface BREAnalytics {
  total: number;
  bre1Passed: number;
  bre1Failed: number;
  bre2Passed: number;
  bre2Failed: number;
  overallPassed: number;
  overallFailed: number;
  passRate: number;
  stageBreakdown: StageBreakdownRow[];
  // Caprion final status breakdown (from CaprionRequestResponse audit trail)
  caprionApproved: number;
  caprionRejected: number;
  caprionPending: number;
  caprionOpsVerification: number;
  caprionCreated: number;
  caprionTotal: number;
}

interface PostBREAnalytics {
  total: number;
  completed: number;
  dropOff: number;
  conversionRate: number;
  stageBreakdown: StageBreakdownRow[];
}

interface DisbursedAnalytics {
  total: number;
  totalAmount: number;
  avgAmount: number;
  lenderBreakdown: { lender: string; count: number; amount: number }[];
}

// ─── Shared Analytics KPI Card ────────────────────────────────────────────────

const AnalyticsKPICard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  accent: string;
  icon?: React.ReactNode;
}> = ({ label, value, sub, accent, icon }) => (
  <div className={`rounded-xl p-4 bg-gradient-to-br ${accent} shadow-sm border border-white/60`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-xs font-medium text-white/80 truncate">{label}</p>
        <p className="text-2xl font-bold text-white mt-0.5 leading-none">{value}</p>
        {sub && <p className="text-xs text-white/70 mt-1 truncate">{sub}</p>}
      </div>
      {icon && <div className="text-white/60 shrink-0">{icon}</div>}
    </div>
  </div>
);

// ─── Styled Recharts Tooltip ──────────────────────────────────────────────────

const AnalyticsTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color ?? p.fill }} className="font-medium">
          {p.name ?? 'Count'}: <span className="text-gray-800">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

// ─── PreBREAnalyticsPanel ─────────────────────────────────────────────────────

const PreBREAnalyticsPanel: React.FC<{ data: PreBREAnalytics }> = ({ data }) => (
  <div className="mt-4 space-y-4">
    {/* KPI Grid */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <AnalyticsKPICard label="Total Entered" value={data.total} accent="from-blue-500 to-blue-600" icon={<Users className="h-5 w-5" />} />
      <AnalyticsKPICard label="Reached BRE" value={data.completed} sub={`${data.conversionRate.toFixed(1)}% conversion`} accent="from-blue-400 to-blue-500" />
      <AnalyticsKPICard label="Dropped Off" value={data.dropOff} sub={`${(100 - data.conversionRate).toFixed(1)}% drop-off`} accent="from-slate-500 to-slate-600" />
      <AnalyticsKPICard label="Avg Stage" value={data.avgStage.toFixed(1)} sub="of Pre-BRE stages" accent="from-blue-600 to-indigo-600" />
    </div>

    {/* Chart + Table */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Bar Chart */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">Stage Distribution</p>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data.stageBreakdown} margin={{ top: 4, right: 8, left: -20, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} angle={-35} textAnchor="end" interval={0} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
            <Tooltip content={<AnalyticsTooltip />} />
            <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={32} name="Users" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Stage Breakdown Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <p className="text-xs font-semibold text-gray-600 px-4 pt-4 pb-2 uppercase tracking-wide">Stage Breakdown</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-blue-50 text-blue-700">
                <th className="text-left px-4 py-2 font-semibold">Stage</th>
                <th className="text-right px-4 py-2 font-semibold">Users</th>
                <th className="text-right px-4 py-2 font-semibold">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {data.stageBreakdown.map((row, i) => (
                <tr key={row.name || row.stage} className={i % 2 === 0 ? 'bg-white hover:bg-blue-50/40' : 'bg-gray-50/60 hover:bg-blue-50/40'}>
                  <td className="px-4 py-2 text-gray-700 font-medium">{row.name}</td>
                  <td className="px-4 py-2 text-right font-bold text-blue-700">{row.count}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{row.pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

// ─── BREAnalyticsPanel ────────────────────────────────────────────────────────

const BREAnalyticsPanel: React.FC<{
  data: BREAnalytics;
  pipelineUsers: ApprovedPipelineUser[];
  pipelineExpanded: boolean;
  onTogglePipeline: () => void;
}> = ({ data, pipelineUsers, pipelineExpanded, onTogglePipeline }) => {
  // BREAnalyticsPanel is Caprion-specific — only show pipeline entries that
  // belong to Caprion.  The backend currently returns only Caprion-approved
  // users (it queries CaprionRequestResponse), so lender_code is absent/null
  // on all current rows and the filter is a transparent no-op today.  When
  // the backend is extended to include multi-lender pipeline rows, this guard
  // will correctly scope the panel to Caprion only.
  const caprionPipelineUsers = pipelineUsers.filter(
    u => !u.lender_code || u.lender_code.toUpperCase() === 'CAPRION',
  );
  const caprionPipelineTotal = caprionPipelineUsers.reduce(
    (sum, u) => sum + (u.approved_loan_amount ?? u.loan_amount ?? 0),
    0,
  );

  const donutData = [
    { name: 'Passed', value: data.overallPassed, fill: '#f97316' },
    { name: 'Failed', value: data.overallFailed, fill: '#fde68a' },
  ];
  const caprionDonutData = data.caprionTotal > 0 ? [
    { name: 'Approved', value: data.caprionApproved, fill: '#22c55e' },
    { name: 'Rejected', value: data.caprionRejected, fill: '#ef4444' },
    { name: 'Pending',  value: data.caprionPending,  fill: '#f59e0b' },
    { name: 'OPS Verify', value: data.caprionOpsVerification, fill: '#3b82f6' },
    { name: 'Created',  value: data.caprionCreated,  fill: '#94a3b8' },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="mt-4 space-y-4">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <AnalyticsKPICard label="Total in BRE" value={data.total} accent="from-orange-500 to-orange-600" icon={<Users className="h-5 w-5" />} />
        <AnalyticsKPICard label="Overall Passed" value={data.overallPassed} sub={`${data.passRate.toFixed(1)}% pass rate`} accent="from-orange-400 to-amber-500" />
        <AnalyticsKPICard label="Overall Failed" value={data.overallFailed} sub={`${(100 - data.passRate).toFixed(1)}% fail rate`} accent="from-red-500 to-red-600" />
        <AnalyticsKPICard label="Pass Rate" value={`${data.passRate.toFixed(1)}%`} sub="BRE1 + BRE2 combined" accent="from-orange-600 to-red-600" />
      </div>

      {/* Caprion Final Status breakdown — only shown when there are Caprion users */}
      {data.caprionTotal > 0 && (
        <div className="rounded-xl border border-orange-200 bg-orange-50/40 p-4">
          <p className="text-xs font-bold text-orange-700 uppercase tracking-wide mb-3">
            Caprion Final Stage Status
            <span className="ml-2 font-normal text-orange-500 normal-case">({data.caprionTotal} users with lender decision)</span>
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{data.caprionApproved}</p>
              <p className="text-xs text-green-600 mt-0.5 font-medium">APPROVED</p>
            </div>
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-center">
              <p className="text-2xl font-bold text-red-700">{data.caprionRejected}</p>
              <p className="text-xs text-red-600 mt-0.5 font-medium">REJECTED</p>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{data.caprionPending}</p>
              <p className="text-xs text-amber-600 mt-0.5 font-medium">PENDING</p>
            </div>
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{data.caprionOpsVerification}</p>
              <p className="text-xs text-blue-600 mt-0.5 font-medium">OPS VERIFY</p>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
              <p className="text-2xl font-bold text-slate-700">{data.caprionCreated}</p>
              <p className="text-xs text-slate-600 mt-0.5 font-medium">CREATED</p>
            </div>
          </div>
          {caprionDonutData.length > 0 && (
            <div className="mt-3 h-1.5 rounded-full bg-orange-100 overflow-hidden flex">
              {caprionDonutData.map(d => (
                <div
                  key={d.name}
                  className="h-full transition-all duration-500"
                  style={{ width: `${(d.value / data.caprionTotal) * 100}%`, backgroundColor: d.fill }}
                  title={`${d.name}: ${d.value}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* BRE1 / BRE2 Sub-cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-4">
          <p className="text-xs font-bold text-orange-700 uppercase tracking-wide mb-3">BRE 1</p>
          <div className="flex gap-4">
            <div className="flex-1 text-center">
              <p className="text-2xl font-bold text-green-600">{data.bre1Passed}</p>
              <p className="text-xs text-gray-500 mt-0.5">Passed</p>
            </div>
            <div className="w-px bg-orange-200" />
            <div className="flex-1 text-center">
              <p className="text-2xl font-bold text-red-500">{data.bre1Failed}</p>
              <p className="text-xs text-gray-500 mt-0.5">Failed</p>
            </div>
          </div>
          {data.bre1Passed + data.bre1Failed > 0 && (
            <div className="mt-3 h-1.5 rounded-full bg-orange-200 overflow-hidden">
              <div className="h-full bg-green-500 rounded-full transition-all duration-500"
                style={{ width: `${(data.bre1Passed / (data.bre1Passed + data.bre1Failed)) * 100}%` }} />
            </div>
          )}
        </div>
        <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-4">
          <p className="text-xs font-bold text-orange-700 uppercase tracking-wide mb-3">BRE 2</p>
          <div className="flex gap-4">
            <div className="flex-1 text-center">
              <p className="text-2xl font-bold text-green-600">{data.bre2Passed}</p>
              <p className="text-xs text-gray-500 mt-0.5">Passed</p>
            </div>
            <div className="w-px bg-orange-200" />
            <div className="flex-1 text-center">
              <p className="text-2xl font-bold text-red-500">{data.bre2Failed}</p>
              <p className="text-xs text-gray-500 mt-0.5">Failed</p>
            </div>
          </div>
          {data.bre2Passed + data.bre2Failed > 0 && (
            <div className="mt-3 h-1.5 rounded-full bg-orange-200 overflow-hidden">
              <div className="h-full bg-green-500 rounded-full transition-all duration-500"
                style={{ width: `${(data.bre2Passed / (data.bre2Passed + data.bre2Failed)) * 100}%` }} />
            </div>
          )}
        </div>
      </div>

      {/* Donut + Stage Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col items-center">
          <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide self-start">Pass / Fail Split</p>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={donutData} cx="50%" cy="50%" innerRadius={50} outerRadius={75}
                dataKey="value" paddingAngle={3} stroke="none">
                {donutData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Tooltip formatter={(v: any, n: any) => [v, n]} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <p className="text-xs font-semibold text-gray-600 px-4 pt-4 pb-2 uppercase tracking-wide">Stage Breakdown</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-orange-50 text-orange-700">
                  <th className="text-left px-4 py-2 font-semibold">Stage</th>
                  <th className="text-right px-4 py-2 font-semibold">Users</th>
                  <th className="text-right px-4 py-2 font-semibold">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {data.stageBreakdown.map((row, i) => (
                  <tr key={row.name || row.stage} className={i % 2 === 0 ? 'bg-white hover:bg-orange-50/40' : 'bg-gray-50/60 hover:bg-orange-50/40'}>
                    <td className="px-4 py-2 text-gray-700 font-medium">{row.name}</td>
                    <td className="px-4 py-2 text-right font-bold text-orange-700">{row.count}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{row.pct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* BRE Conversion Flow */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">BRE Conversion Flow</p>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { label: 'Entered BRE', value: data.total, color: 'bg-orange-100 text-orange-800 border-orange-300' },
            { label: 'BRE1 Pass', value: data.bre1Passed, color: 'bg-green-100 text-green-800 border-green-300' },
            { label: 'BRE2 Pass', value: data.bre2Passed, color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
            { label: 'Overall Pass', value: data.overallPassed, color: 'bg-blue-100 text-blue-800 border-blue-300' },
          ].map((step, i, arr) => (
            <React.Fragment key={step.label}>
              <div className={`flex flex-col items-center px-4 py-2 rounded-lg border ${step.color} min-w-[90px]`}>
                <span className="text-xl font-bold">{step.value}</span>
                <span className="text-xs mt-0.5 text-center leading-tight">{step.label}</span>
              </div>
              {i < arr.length - 1 && (
                <div className="flex flex-col items-center text-gray-400">
                  <span className="text-lg">→</span>
                  {i < arr.length - 1 && arr[i + 1].value > 0 && step.value > 0 && (
                    <span className="text-xs text-gray-400">{((arr[i + 1].value / step.value) * 100).toFixed(0)}%</span>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      {/* ── Approved — Pending Disbursal (within BRE Analytics panel) ──── */}
      <Card className="border-t-4 border-amber-400">
        <CardHeader
          className="bg-amber-50 cursor-pointer select-none py-3 px-4"
          onClick={onTogglePipeline}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base font-semibold text-amber-700">
                ⏳ Approved — Pending Disbursal
              </CardTitle>
              <Badge className="bg-amber-100 text-amber-800 text-xs font-bold">
                {caprionPipelineUsers.length}
              </Badge>
              {caprionPipelineTotal > 0 && (
                <span className="text-sm font-semibold text-amber-700">
                  {fmtRupeeIN(caprionPipelineTotal)}
                </span>
              )}
              <span className="text-xs text-gray-500">sanctioned, awaiting disbursal</span>
            </div>
            <div className="flex items-center gap-2">
              {pipelineExpanded
                ? <ChevronUp className="h-4 w-4 text-gray-500" />
                : <ChevronDown className="h-4 w-4 text-gray-500" />}
            </div>
          </div>
        </CardHeader>

        {pipelineExpanded && (
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50/50">
                    <TableHead className="text-xs whitespace-nowrap">Name</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Phone</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">BAP</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Approved Amt</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Loan Amt</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Current Stage</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Last Updated</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {caprionPipelineUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-4 text-gray-400 italic">
                        No pending disbursals
                      </TableCell>
                    </TableRow>
                  ) : (
                    caprionPipelineUsers.map((p, idx) => (
                      <TableRow key={p.user_id ?? idx} className="hover:bg-amber-50/30">
                        <TableCell className="font-medium text-sm whitespace-nowrap">
                          {p.name || '—'}
                        </TableCell>
                        <TableCell className="font-mono text-xs whitespace-nowrap">
                          {p.phone || '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {p.bap || '—'}
                        </TableCell>
                        <TableCell className="text-sm font-semibold text-amber-700 whitespace-nowrap">
                          {p.approved_loan_amount != null
                            ? fmtRupeeIN(p.approved_loan_amount)
                            : p.loan_amount != null ? fmtRupeeIN(p.loan_amount) : '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {p.loan_amount != null ? fmtRupeeIN(p.loan_amount) : '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap text-gray-500">
                          {p.stage_name || '—'}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                          {p.last_updated ? toIST(p.last_updated) : '—'}
                        </TableCell>
                        {/* On-demand live CheckStatus — see PipelineCheckStatusCell. */}
                        <TableCell className="text-xs whitespace-nowrap">
                          <PipelineCheckStatusCell user={p} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

// ─── PipelineCheckStatusCell ──────────────────────────────────────────────────
//
// Per-row "Check Status" button for the Approved — Pending Disbursal table,
// mirroring the one on the BRE Analysis screen.
//
// WHY ITS OWN COMPONENT: this table is rendered in TWO places in this file (the
// copy inside BREAnalyticsPanel and the standalone one further down). One
// definition means both behave identically, and each row owns its OWN
// loading/result state — so clicking one row can never spin or overwrite
// another.
//
// SAFETY:
//   * Calls Caprion's LIVE CheckStatus API, so it fires ONLY on click — never on
//     render, and never on this dashboard's 30s auto-refresh.
//   * `application_id` is optional on ApprovedPipelineUser because the dashboard
//     falls back to the funnel API's pipeline list when the dedicated pipeline
//     endpoint fails, and that payload has no application_id. Without one the
//     button is disabled rather than calling the lender with a blank id.
//   * Uses `adminService.caprionCheckStatus` — the SAME call the BRE Analysis
//     screen's working button makes. A raw relative `fetch('/api/...')` is NOT
//     equivalent: adminApi prefixes API_BASE_URL and carries the axios auth/CSRF
//     setup, so on a deployed environment (where the frontend and API are not
//     the same origin) the relative fetch returns an error while this works.
const PipelineCheckStatusCell: React.FC<{ user: ApprovedPipelineUser }> = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus]   = useState<string | null>(null);
  const [error, setError]     = useState<string | null>(null);

  const applicationId = (user.application_id || '').trim();

  const run = async () => {
    if (!applicationId) return;
    setLoading(true);
    setStatus(null);
    setError(null);
    try {
      const res = await adminService.caprionCheckStatus({ application_id: applicationId });
      const body: any = res.data ?? {};
      // Caprion nests the human-readable state at data.status.status; the outer
      // `status` is the block itself. Fall through the shallower shapes so a
      // payload variant still renders something instead of a blank cell.
      const resolved =
        body?.data?.status?.status
        ?? body?.status?.status
        ?? (typeof body?.data?.status === 'string' ? body.data.status : null);
      if (typeof resolved === 'string' && resolved) {
        setStatus(resolved);
      } else {
        setError(body?.error ? String(body.error) : 'No status in response');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error ?? (e instanceof Error ? e.message : 'Request failed'));
    } finally {
      setLoading(false);
    }
  };

  const tone = (value: string) => {
    const s = value.toLowerCase();
    if (s.includes('approv'))  return 'bg-green-100 text-green-800';
    if (s.includes('reject') || s.includes('withdraw') || s.includes('cancel'))
      return 'bg-red-100 text-red-800';
    if (s.includes('disburs')) return 'bg-blue-100 text-blue-800';
    return 'bg-amber-100 text-amber-800';
  };

  if (status) {
    return (
      <div className="flex items-center gap-2">
        <Badge className={`text-xs font-semibold ${tone(status)}`}>{status}</Badge>
        <button type="button" title="Re-check"
          className="text-gray-400 hover:text-gray-700"
          onClick={run} disabled={loading}>
          <RefreshCw className="h-3 w-3" />
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-red-600" title={error}>Failed</span>
        <button type="button" title="Retry"
          className="text-gray-400 hover:text-gray-700"
          onClick={run} disabled={loading}>
          <RefreshCw className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 text-xs"
      onClick={run}
      disabled={loading || !applicationId}
      title={applicationId
        ? `Call Caprion CheckStatus for application ${applicationId}`
        : 'No Caprion application ID on record for this row'}
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Check Status'}
    </Button>
  );
};

// ─── PostBREAnalyticsPanel ────────────────────────────────────────────────────

const PostBREAnalyticsPanel: React.FC<{ data: PostBREAnalytics }> = ({ data }) => (
  <div className="mt-4 space-y-4">
    {/* KPI Grid */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <AnalyticsKPICard label="Total Entered" value={data.total} accent="from-green-500 to-green-600" icon={<Users className="h-5 w-5" />} />
      <AnalyticsKPICard label="Completed" value={data.completed} sub={`${data.conversionRate.toFixed(1)}% completion`} accent="from-green-400 to-emerald-500" />
      <AnalyticsKPICard label="Dropped Off" value={data.dropOff} sub={`${(100 - data.conversionRate).toFixed(1)}% drop-off`} accent="from-slate-500 to-slate-600" />
      <AnalyticsKPICard label="Conversion" value={`${data.conversionRate.toFixed(1)}%`} sub="Post-BRE → Completed" accent="from-green-600 to-teal-600" />
    </div>

    {/* Chart + Table */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">Stage Distribution</p>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data.stageBreakdown} margin={{ top: 4, right: 8, left: -20, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} angle={-35} textAnchor="end" interval={0} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
            <Tooltip content={<AnalyticsTooltip />} />
            <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={32} name="Users" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <p className="text-xs font-semibold text-gray-600 px-4 pt-4 pb-2 uppercase tracking-wide">Stage Breakdown</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-green-50 text-green-700">
                <th className="text-left px-4 py-2 font-semibold">Stage</th>
                <th className="text-right px-4 py-2 font-semibold">Users</th>
                <th className="text-right px-4 py-2 font-semibold">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {data.stageBreakdown.map((row, i) => (
                <tr key={row.name || row.stage} className={i % 2 === 0 ? 'bg-white hover:bg-green-50/40' : 'bg-gray-50/60 hover:bg-green-50/40'}>
                  <td className="px-4 py-2 text-gray-700 font-medium">{row.name}</td>
                  <td className="px-4 py-2 text-right font-bold text-green-700">{row.count}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{row.pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

// ─── DisbursedAnalyticsPanel ──────────────────────────────────────────────────

const DisbursedAnalyticsPanel: React.FC<{ data: DisbursedAnalytics }> = ({ data }) => {
  const lenderChartData = data.lenderBreakdown.map(l => ({ name: l.lender || 'Unknown', count: l.count, amount: l.amount }));
  return (
    <div className="mt-4 space-y-4">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <AnalyticsKPICard label="Total Disbursed" value={data.total} accent="from-emerald-500 to-emerald-600" icon={<DollarSign className="h-5 w-5" />} />
        <AnalyticsKPICard label="Total Amount" value={fmtRupeeIN(data.totalAmount)} sub="across all disbursals" accent="from-emerald-400 to-teal-500" />
        <AnalyticsKPICard label="Avg Loan Size" value={data.total > 0 ? fmtRupeeIN(data.avgAmount) : '—'} sub="per disbursal" accent="from-teal-500 to-teal-600" />
        <AnalyticsKPICard label="Lenders Active" value={data.lenderBreakdown.length} sub="in this period" accent="from-emerald-600 to-green-700" />
      </div>

      {/* Lender Chart + Breakdown Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">Disbursals by Lender</p>
          {lenderChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={lenderChartData} margin={{ top: 4, right: 8, left: -20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
                <Tooltip content={<AnalyticsTooltip />} />
                <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} name="Disbursals" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-sm text-gray-400">No data</div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <p className="text-xs font-semibold text-gray-600 px-4 pt-4 pb-2 uppercase tracking-wide">Lender Breakdown</p>
          {data.lenderBreakdown.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">No disbursals in range</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-emerald-50 text-emerald-700">
                    <th className="text-left px-4 py-2 font-semibold">Lender</th>
                    <th className="text-right px-4 py-2 font-semibold">Count</th>
                    <th className="text-right px-4 py-2 font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lenderBreakdown.map((row, i) => (
                    <tr key={row.lender} className={i % 2 === 0 ? 'bg-white hover:bg-emerald-50/40' : 'bg-gray-50/60 hover:bg-emerald-50/40'}>
                      <td className="px-4 py-2 text-gray-700 font-medium">{row.lender || 'Unknown'}</td>
                      <td className="px-4 py-2 text-right font-bold text-emerald-700">{row.count}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{fmtRupeeIN(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── FunnelDashboard (main export) ───────────────────────────────────────────

const FunnelDashboard: React.FC = () => {
  // Tab state — 'funnel' | 'phonepe_mis' | 'drop_off_alerts'
  const [activeTab, setActiveTab] = useState<'funnel' | 'phonepe_mis' | 'drop_off_alerts'>('funnel');

  // Lender filter — '' means "All Lenders"; otherwise the BAP name string
  // (e.g. 'PetPooja' or 'PhonePe') is used to filter users and disbursed_users.
  const [selectedLender, setSelectedLender] = useState<string>('');

  const [selectedDate, setSelectedDate]       = useState<DateFilter>('today');
  const [customDateFrom, setCustomDateFrom]   = useState('');
  const [customDateTo, setCustomDateTo]       = useState('');
  const [selectedStage, setSelectedStage]     = useState<StageCount | null>(null);
  const [isModalOpen, setIsModalOpen]         = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [currentPage, setCurrentPage]         = useState(1);
  const [expandedBuckets, setExpandedBuckets] = useState({ pre_bre: true, bre: true, post_bre: true });
  const [tableSortAsc, setTableSortAsc]       = useState(false);

  // ── Latest Journeys table filters ────────────────────────────────────────────
  const [filterBucket,    setFilterBucket]    = useState<string>('');
  const [filterStage,     setFilterStage]     = useState<string>('');
  const [filterSourceSlug,setFilterSourceSlug]= useState<string>('');
  const [filterFinalStage,setFilterFinalStage]= useState<string>('');

  // BRE Reject Modal state
  const [breModalUser, setBreModalUser]     = useState<JourneyUser | null>(null);
  const [isBreModalOpen, setIsBreModalOpen] = useState(false);

  // Caprion JSON Viewer Modal state. The call ID is tracked SEPARATELY from the
  // user because one row has up to three viewable bodies (final status, BRE1,
  // BRE2) and the modal fetches whichever one was clicked — the list response
  // carries only IDs now, not the bodies.
  const [jsonViewerUser, setJsonViewerUser]     = useState<JourneyUser | null>(null);
  const [jsonViewerId, setJsonViewerId]         = useState<number | null>(null);
  const [isJsonViewerOpen, setIsJsonViewerOpen] = useState(false);

  const [allUsers, setAllUsers]           = useState<JourneyUser[]>([]);
  const [serverTotal, setServerTotal]     = useState(0);
  // stage_counts returned by the server (computed from the full filtered set,
  // not just the current page) — used for charts so they always show the
  // complete picture regardless of which page is loaded.
  const [serverStageCounts, setServerStageCounts] = useState<Array<{
    stageName: string; stage: number; stageKey: string;
    bucket: string; subBucket: string | null; count: number;
  }>>([]);
  const [usingMock, setUsingMock]         = useState(false);
  const [loading, setLoading]             = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

  // Disbursement state
  const [disbursedCount, setDisbursedCount]               = useState(0);
  const [disbursedAmountTotal, setDisbursedAmountTotal]   = useState(0);
  const [disbursedUsers, setDisbursedUsers]               = useState<DisbursedUser[]>([]);
  const [disbursedExpanded, setDisbursedExpanded]         = useState(false);
  const [isDisbursedModalOpen, setIsDisbursedModalOpen]   = useState(false);

  // Approved-but-not-yet-disbursed pipeline state
  const [pipelineUsers, setPipelineUsers]                 = useState<ApprovedPipelineUser[]>([]);
  const [pipelineTotal, setPipelineTotal]                 = useState(0);
  const [pipelineExpanded, setPipelineExpanded]           = useState(false);

  // Analytics panel open/close toggles
  const [showPreBREAnalytics,  setShowPreBREAnalytics]  = useState(false);
  const [showBREAnalytics,     setShowBREAnalytics]     = useState(false);
  const [showPostBREAnalytics, setShowPostBREAnalytics] = useState(false);
  const [showDisbAnalytics,    setShowDisbAnalytics]    = useState(false);

  // Compute date range strings for the MIS tab (reuse same date filter state)
  const computeDateRange = useCallback(() => {
    const today = todayIST();
    let dateFrom = today;
    let dateTo   = today;
    if (selectedDate === 'yesterday') {
      dateFrom = dateTo = offsetISTDate(today, -1);
    } else if (selectedDate === 'last7days') {
      dateFrom = offsetISTDate(today, -7);
    } else if (selectedDate === 'last30days') {
      dateFrom = offsetISTDate(today, -30);
    } else if (selectedDate === 'custom' && customDateFrom && customDateTo) {
      dateFrom = customDateFrom;
      dateTo   = customDateTo;
    }
    return { dateFrom, dateTo };
  }, [selectedDate, customDateFrom, customDateTo]);

  const { dateFrom: misDf, dateTo: misDt } = computeDateRange();

  // ── Debounced search: wait 400 ms after the user stops typing before
  //    triggering a new server fetch.  loadData depends on debouncedSearch,
  //    not searchQuery, so the API is not hammered on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Reset to page 1 whenever the date range or search term changes so the
  // user always lands on the first page of the new result set.
  useEffect(() => { setCurrentPage(1); }, [selectedDate, customDateFrom, customDateTo, debouncedSearch]);

  const [pageSize, setPageSize] = useState(25);
  const PAGE_SIZE = pageSize;

  const loadData = useCallback(async () => {
    try {
      const today = todayIST();
      let dateFrom = today;
      let dateTo   = today;

      if (selectedDate === 'yesterday') {
        dateFrom = dateTo = offsetISTDate(today, -1);
      } else if (selectedDate === 'last7days') {
        dateFrom = offsetISTDate(today, -7);
      } else if (selectedDate === 'last30days') {
        dateFrom = offsetISTDate(today, -30);
      } else if (selectedDate === 'custom' && customDateFrom && customDateTo) {
        dateFrom = customDateFrom;
        dateTo   = customDateTo;
      }

      // No page/page_size — fetch all matching users so client-side filters
      // (bucket, stage, source_slug, final_stage, lender) work on the full set.
      const params = new URLSearchParams({
        date_from: dateFrom,
        date_to:   dateTo,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);

      try {
        // Fire both requests in parallel:
        //   1. journey_funnel_data — main funnel table + disbursed section
        //   2. approved_pipeline   — dedicated API (LoanJourneyState-based,
        //      catches NULL-user_id CRR rows the funnel API misses)
        const [apiRes, pipelineRes] = await Promise.all([
          fetch(
            `/api/admin/journey_funnel_data/?${params.toString()}`,
            { credentials: 'include', cache: 'no-store' },
          ),
          fetch(
            '/api/lms/staff_ops/disbursed_loans/approved_pipeline/',
            { credentials: 'include', cache: 'no-store' },
          ),
        ]);

        if (apiRes.ok) {
          const apiData = await apiRes.json();
          if (apiData.users && Array.isArray(apiData.users)) {
            setAllUsers(apiData.users as JourneyUser[]);
            setServerTotal(apiData.total ?? apiData.users.length);
            setServerStageCounts(apiData.stage_counts ?? []);
            setDisbursedCount(apiData.disbursed_count || 0);
            setDisbursedAmountTotal(apiData.disbursed_amount_total || 0);
            setDisbursedUsers(apiData.disbursed_users || []);

            // Use the dedicated pipeline API response when available — it uses
            // LoanJourneyState as the source of truth and resolves Caprion status
            // via both user_id and bap_lead_detail_fkey (NULL-user_id fallback),
            // so it catches users the funnel API's pipeline section misses.
            if (pipelineRes.ok) {
              try {
                const pipelineData = await pipelineRes.json();
                // Dedicated API shape: { count, total_approved_amount, results: [...] }
                // Map to the same shape the rest of the dashboard expects:
                //   approved_pipeline_users / approved_pipeline_total
                const pipelineUsers = pipelineData.results ?? [];
                const pipelineTotal = pipelineData.total_approved_amount
                  ? parseFloat(pipelineData.total_approved_amount)
                  : 0;
                setPipelineUsers(pipelineUsers);
                setPipelineTotal(pipelineTotal);
              } catch {
                // Pipeline API parse error — fall back to funnel data's pipeline
                setPipelineUsers(apiData.approved_pipeline_users || []);
                setPipelineTotal(apiData.approved_pipeline_total || 0);
              }
            } else {
              // Pipeline API unavailable — fall back to funnel data's pipeline
              setPipelineUsers(apiData.approved_pipeline_users || []);
              setPipelineTotal(apiData.approved_pipeline_total || 0);
            }

            setUsingMock(false);
            setLastRefreshed(new Date());
            return;
          }
        }
      } catch {
        // API unavailable — fall through to empty state
      }

      setAllUsers([]);
      setServerTotal(0);
      setServerStageCounts([]);
      setDisbursedCount(0);
      setDisbursedAmountTotal(0);
      setDisbursedUsers([]);
      setPipelineUsers([]);
      setPipelineTotal(0);
      setUsingMock(false);
      setLastRefreshed(new Date());
    } catch {
      setAllUsers([]);
      setServerTotal(0);
      setServerStageCounts([]);
      setDisbursedCount(0);
      setDisbursedAmountTotal(0);
      setDisbursedUsers([]);
      setPipelineUsers([]);
      setPipelineTotal(0);
      setUsingMock(false);
      setLastRefreshed(new Date());
    }
  }, [selectedDate, customDateFrom, customDateTo, debouncedSearch]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadData]);

  // ── Lender options derived from live data ────────────────────────────────────
  // Build the list of unique BAP names present in the current data set so the
  // dropdown only shows lenders that actually have users in the selected range.
  const lenderOptions = useMemo(() => {
    const bapSet = new Set<string>();
    allUsers.forEach(u => { if (u.bap) bapSet.add(u.bap); });
    disbursedUsers.forEach(d => {
      const bap = LENDER_CODE_TO_BAP[d.lender_code];
      if (bap) bapSet.add(bap);
    });
    return Array.from(bapSet).sort();
  }, [allUsers, disbursedUsers]);

  const dateFilteredUsers = useMemo(
    () => usingMock
      ? applyDateFilter(allUsers, selectedDate, customDateFrom, customDateTo)
      : allUsers,
    [allUsers, usingMock, selectedDate, customDateFrom, customDateTo],
  );

  // Apply lender filter on top of date filter
  const filteredUsers = useMemo(
    () => selectedLender
      ? dateFilteredUsers.filter(u => u.bap === selectedLender)
      : dateFilteredUsers,
    [dateFilteredUsers, selectedLender],
  );

  // Filtered disbursed users — filter by bap field (direct BAP name, same as u.bap on
  // journey users) with lender_code→BAP mapping as a fallback for older API responses.
  const filteredDisbursedUsers = useMemo(
    () => selectedLender
      ? disbursedUsers.filter(d => {
          // Prefer the bap field (direct BAP name match, same as u.bap on journey users)
          if (d.bap) return d.bap === selectedLender;
          // Fallback: lender_code → BAP name mapping
          return LENDER_CODE_TO_BAP[d.lender_code] === selectedLender;
        })
      : disbursedUsers,
    [disbursedUsers, selectedLender],
  );

  // Filtered disbursed count / amount total (derived from filteredDisbursedUsers)
  const filteredDisbursedCount = filteredDisbursedUsers.length;
  // Sum loan_amount (sanctioned amount) rather than disbursed_amount (actual
  // transferred amount) — the card/section total should reflect the loan value.
  const filteredDisbursedAmountTotal = filteredDisbursedUsers.reduce(
    (sum, d) => sum + (d.loan_amount ?? 0), 0,
  );

  // ── stageCounts: always built from filteredUsers (client-side) so that the
  //    lender filter is correctly reflected in the summary cards, chart, and
  //    bucket panels.  serverStageCounts is used as the source-of-truth for
  //    bucket/subBucket classification metadata only (not for counts).
  const stageCounts = useMemo((): StageCount[] => {
    // Stage names that the API returns as "pre_bre" but belong in the BRE bucket (bre2_aa).
    const BRE2_AA_STAGE_NAME_OVERRIDES = new Set([
      'AA Verification',
    ]);
    // Stage names that the API may return as "pre_bre" or "bre" but belong in post_bre.
    const POST_BRE_STAGE_NAME_OVERRIDES = new Set([
      'Aadhaar Verification',
      'Identity Verification',
      'Final Verification',
      'Agreement PDF Display',
      'Agreement Signing',
      'Dealer Approval Validation',
      'Customize Loan',
      'Final Loan Offer',
      'Final Offer',
      'Loan Validation',
      'Loan Details',
      'Loan Verification',
    ]);

    // Build a metadata map from serverStageCounts for bucket/subBucket classification.
    const metaMap: Record<string, { stage: number; stageKey: string; bucket: string; subBucket: string | null }> = {};
    for (const sc of serverStageCounts) {
      const bucket = POST_BRE_STAGE_NAME_OVERRIDES.has(sc.stageName) ? 'post_bre'
        : BRE2_AA_STAGE_NAME_OVERRIDES.has(sc.stageName) ? 'bre'
        : sc.bucket;
      const subBucket = POST_BRE_STAGE_NAME_OVERRIDES.has(sc.stageName) ? null
        : BRE2_AA_STAGE_NAME_OVERRIDES.has(sc.stageName) ? 'bre2_aa'
        : sc.subBucket;
      metaMap[sc.stageName] = {
        stage:     sc.stage,
        stageKey:  sc.stageKey || `STAGE_${sc.stage}`,
        bucket,
        subBucket,
      };
    }

    // Group filteredUsers by stageName — counts and users both respect the lender filter.
    const grouped: Record<string, { users: JourneyUser[]; meta: typeof metaMap[string] }> = {};
    for (const u of filteredUsers) {
      const sn = u.stageName;
      if (!grouped[sn]) {
        // Derive bucket from metaMap if available, otherwise from the user's own bucket field.
        const meta = metaMap[sn] ?? {
          stage:     u.stage,
          stageKey:  u.stageKey,
          bucket:    POST_BRE_STAGE_NAME_OVERRIDES.has(sn) ? 'post_bre'
                     : BRE2_AA_STAGE_NAME_OVERRIDES.has(sn) ? 'bre'
                     : u.bucket,
          subBucket: POST_BRE_STAGE_NAME_OVERRIDES.has(sn) ? null
                     : BRE2_AA_STAGE_NAME_OVERRIDES.has(sn) ? 'bre2_aa'
                     : (u.subBucket ?? null),
        };
        grouped[sn] = { users: [], meta };
      }
      grouped[sn].users.push(u);
    }

    const raw: StageCount[] = Object.entries(grouped).map(([stageName, { users: stageUsers, meta }]) => ({
      stage:     meta.stage,
      stageKey:  meta.stageKey,
      stageName,
      count:     stageUsers.length,
      users:     stageUsers,
      bucket:    meta.bucket,
      subBucket: meta.subBucket ?? undefined,
    } as StageCount)).sort((a, b) => a.stage - b.stage);

    return raw.map((sc, idx) => {
      const prev = raw[idx - 1];
      const dropOffRate = prev && prev.count > 0 ? ((prev.count - sc.count) / prev.count) * 100 : undefined;
      return { ...sc, dropOffRate };
    });
  }, [serverStageCounts, filteredUsers]);

  const preBre  = stageCounts.filter(sc => sc.bucket === 'pre_bre');
  const bre     = stageCounts.filter(sc => sc.bucket === 'bre');
  const postBre = stageCounts.filter(sc => sc.bucket === 'post_bre');

  const preBreTot  = preBre.reduce((s, sc) => s + sc.count, 0);
  const breTot     = bre.reduce((s, sc) => s + sc.count, 0);
  const postBreTot = postBre.reduce((s, sc) => s + sc.count, 0);
  // Total = all filteredUsers (respects lender filter)
  const total      = filteredUsers.length;
  const trend      = 0;

  // Completed count — last stage in post_bre bucket (stage 19)
  const completedCount = stageCounts.find(sc => sc.stage === 19)?.count ?? 0;

  const chartData = [
    ...stageCounts.map(sc => ({
      stageName: sc.stageName.length > 12 ? sc.stageName.slice(0, 11) + '…' : sc.stageName,
      stageNameFull: sc.stageName,
      count: sc.count,
      bucket: sc.bucket,
      dropOffRate: sc.dropOffRate,
      fill: BUCKET_META[sc.bucket as BucketKey]?.color ?? '#6b7280',
    })),
    {
      stageName: 'Disbursed',
      stageNameFull: 'Disbursed',
      count: filteredDisbursedCount,
      bucket: 'disbursed',
      // Clamp: disbursed uses a different date anchor (date_of_disbursal) vs the
      // funnel stages (journey creation date), so disbursed can exceed completedCount.
      // Show '—' instead of a negative or >100% drop-off in that case.
      dropOffRate: completedCount > 0 && filteredDisbursedCount <= completedCount
        ? ((completedCount - filteredDisbursedCount) / completedCount) * 100
        : undefined,
      fill: DISBURSED_COLOR,
    },
  ];

  // ── tableUsers: server already applied the search filter and returned only
  //    the current page.  We still apply the remaining client-side dropdown
  //    filters (bucket, stage, source_slug, final_stage) and sort on top.
  const tableUsers = useMemo(() => {
    let result = filteredUsers;
    if (filterBucket)     result = result.filter(u => u.bucket === filterBucket);
    if (filterStage)      result = result.filter(u => u.stageName === filterStage);
    if (filterSourceSlug) result = result.filter(u => (u.source_slug ?? '') === filterSourceSlug);
    if (filterFinalStage) result = result.filter(u => (u.caprion_final_status ?? '') === filterFinalStage);
    return [...result].sort((a, b) => {
      const cmp = a.lastUpdated.localeCompare(b.lastUpdated);
      return tableSortAsc ? cmp : -cmp;
    });
  }, [filteredUsers, tableSortAsc, filterBucket, filterStage, filterSourceSlug, filterFinalStage]);

  // Derived option lists for filter dropdowns — built from the full filteredUsers set
  // (not tableUsers) so options don't disappear when another filter is active.
  const stageOptions     = useMemo(() => [...new Set(filteredUsers.map(u => u.stageName))].sort(), [filteredUsers]);
  const sourceSlugOptions= useMemo(() => [...new Set(filteredUsers.map(u => u.source_slug ?? '').filter(Boolean))].sort(), [filteredUsers]);
  const finalStageOptions= useMemo(() => [...new Set(filteredUsers.map(u => u.caprion_final_status ?? '').filter(Boolean))].sort(), [filteredUsers]);

  const activeFilterCount = [filterBucket, filterStage, filterSourceSlug, filterFinalStage].filter(Boolean).length;
  const clearTableFilters = () => { setFilterBucket(''); setFilterStage(''); setFilterSourceSlug(''); setFilterFinalStage(''); setCurrentPage(1); };

  // ── Analytics data computations ──────────────────────────────────────────────

  const preBREAnalytics = useMemo((): PreBREAnalytics => {
    // total = unique users currently sitting in any pre-BRE stage
    const total = preBre.reduce((s, sc) => s + sc.count, 0);
    // "Reached BRE" = users who have moved past pre-BRE into BRE or Post-BRE
    const completed = breTot + postBreTot;
    // dropOff = users still stuck in pre-BRE (never reached BRE)
    const dropOff = total;
    const conversionRate = (total + completed) > 0 ? (completed / (total + completed)) * 100 : 0;
    const avgStage = preBre.length > 0 && total > 0
      ? preBre.reduce((s, sc) => s + sc.stage * sc.count, 0) / total
      : 0;
    const stageBreakdown: StageBreakdownRow[] = preBre.map(sc => ({
      stage: sc.stage,
      name: sc.stageName.length > 18 ? sc.stageName.slice(0, 17) + '…' : sc.stageName,
      count: sc.count,
      pct: total > 0 ? (sc.count / total) * 100 : 0,
    }));
    return { total, completed, dropOff, conversionRate, avgStage, stageBreakdown };
  }, [preBre, breTot, postBreTot]);

  const breAnalytics = useMemo((): BREAnalytics => {
    const total = bre.reduce((s, sc) => s + sc.count, 0);

    // BRE users in bre1 sub-bucket (stages 6–7: Loan Offer, BRE 1 Result)
    const bre1Users = filteredUsers.filter(u => u.bucket === 'bre' && u.subBucket === 'bre1');
    // BRE users in bre2_aa sub-bucket (stages 8–10: AA Consent, AA Complete, BRE 2 Result)
    const bre2Users = filteredUsers.filter(u => u.bucket === 'bre' && u.subBucket === 'bre2_aa');

    // Backend application_status values: 'BRE_COMPLETED', 'BRE_MANUAL_APPROVAL',
    // 'BRE_REJECTED', 'BRE_ERROR', 'ELIGIBLE_AMOUNT_IS_LESS', null/undefined (pending)
    // BRE1 passed = users who cleared BRE1 and moved to BRE2/AA stages
    const bre1Passed = bre2Users.length + postBreTot;
    // BRE1 failed = users in bre1 stages with a rejected/error status
    const bre1Failed = bre1Users.filter(u =>
      u.application_status === 'BRE_REJECTED' || u.application_status === 'BRE_ERROR',
    ).length;

    // BRE2 passed = users who cleared BRE2 and moved to post-BRE
    const bre2Passed = postBreTot;
    // BRE2 failed = users in bre2_aa stages with a rejected/error status
    const bre2Failed = bre2Users.filter(u =>
      u.application_status === 'BRE_REJECTED' || u.application_status === 'BRE_ERROR',
    ).length;

    // Overall: anyone with BRE_COMPLETED or BRE_MANUAL_APPROVAL across all BRE users
    const overallPassed = filteredUsers.filter(u =>
      u.bucket === 'bre' &&
      (u.application_status === 'BRE_COMPLETED' || u.application_status === 'BRE_MANUAL_APPROVAL'),
    ).length + postBreTot;
    const overallFailed = filteredUsers.filter(u =>
      u.bucket === 'bre' &&
      (u.application_status === 'BRE_REJECTED' || u.application_status === 'BRE_ERROR'),
    ).length;

    const passRate = (overallPassed + overallFailed) > 0
      ? (overallPassed / (overallPassed + overallFailed)) * 100
      : 0;
    const stageBreakdown: StageBreakdownRow[] = bre.map(sc => ({
      stage: sc.stage,
      name: sc.stageName.length > 18 ? sc.stageName.slice(0, 17) + '…' : sc.stageName,
      count: sc.count,
      pct: total > 0 ? (sc.count / total) * 100 : 0,
    }));

    // Caprion final status breakdown — count across ALL filtered users (not just BRE bucket)
    // because the final status is set by the lender and may be visible at any stage.
    const caprionUsers = filteredUsers.filter(u => u.caprion_final_status != null);
    const caprionApproved       = caprionUsers.filter(u => u.caprion_final_status === 'APPROVED').length;
    const caprionRejected       = caprionUsers.filter(u => u.caprion_final_status === 'REJECTED').length;
    const caprionPending        = caprionUsers.filter(u => u.caprion_final_status === 'PENDING').length;
    const caprionOpsVerification = caprionUsers.filter(u => u.caprion_final_status === 'OPS_VERIFICATION').length;
    const caprionCreated        = caprionUsers.filter(u => u.caprion_final_status === 'CREATED').length;
    const caprionTotal          = caprionUsers.length;

    return {
      total, bre1Passed, bre1Failed, bre2Passed, bre2Failed,
      overallPassed, overallFailed, passRate, stageBreakdown,
      caprionApproved, caprionRejected, caprionPending,
      caprionOpsVerification, caprionCreated, caprionTotal,
    };
  }, [bre, filteredUsers, postBreTot]);

  const postBREAnalytics = useMemo((): PostBREAnalytics => {
    // total = unique users currently in any post-BRE stage
    const total = postBre.reduce((s, sc) => s + sc.count, 0);
    // "Completed" = users at the final stage (stage 19 = Completed)
    const completed = stageCounts.find(sc => sc.stage === 19)?.count ?? 0;
    // dropOff = users in post-BRE who haven't reached Completed yet
    const dropOff = total - completed;
    const conversionRate = total > 0 ? (completed / total) * 100 : 0;
    const stageBreakdown: StageBreakdownRow[] = postBre.map(sc => ({
      stage: sc.stage,
      name: sc.stageName.length > 18 ? sc.stageName.slice(0, 17) + '…' : sc.stageName,
      count: sc.count,
      pct: total > 0 ? (sc.count / total) * 100 : 0,
    }));
    return { total, completed, dropOff, conversionRate, stageBreakdown };
  }, [postBre, stageCounts]);

  const disbursedAnalytics = useMemo((): DisbursedAnalytics => {
    const lenderMap: Record<string, { count: number; amount: number }> = {};
    filteredDisbursedUsers.forEach(d => {
      const key = d.lender_code || 'Unknown';
      if (!lenderMap[key]) lenderMap[key] = { count: 0, amount: 0 };
      lenderMap[key].count += 1;
      lenderMap[key].amount += d.disbursed_amount ?? 0;
    });
    const lenderBreakdown = Object.entries(lenderMap)
      .map(([lender, v]) => ({ lender, count: v.count, amount: v.amount }))
      .sort((a, b) => b.count - a.count);
    return {
      total: filteredDisbursedCount,
      totalAmount: filteredDisbursedAmountTotal,
      avgAmount: filteredDisbursedCount > 0 ? filteredDisbursedAmountTotal / filteredDisbursedCount : 0,
      lenderBreakdown,
    };
  }, [filteredDisbursedUsers, filteredDisbursedCount, filteredDisbursedAmountTotal]);

  // Client-side pagination: slice tableUsers by currentPage so all filters
  // (bucket, stage, source_slug, final_stage, lender) work on the full dataset.
  const totalPages = Math.max(1, Math.ceil(tableUsers.length / PAGE_SIZE));
  const pagedUsers = tableUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Open the StageModal with a synthetic StageCount containing all users in a bucket.
  const openBucketModal = (bucketKey: BucketKey | 'all') => {
    let users: JourneyUser[];
    let label: string;
    let bucket: string;
    if (bucketKey === 'all') {
      users = filteredUsers;
      label = 'All Users';
      bucket = 'pre_bre';
    } else {
      users = filteredUsers.filter(u => {
        const resolved = resolveBucket(u.bucket, u.stageName);
        return resolved === bucketKey;
      });
      label = BUCKET_META[bucketKey].label + ' Users';
      bucket = bucketKey;
    }
    const sc: StageCount = {
      stage: 0,
      stageKey: bucketKey,
      stageName: label,
      count: users.length,
      users,
      bucket,
    };
    setSelectedStage(sc);
    setIsModalOpen(true);
  };

  const handleSelectStage = (sc: StageCount) => { setSelectedStage(sc); setIsModalOpen(true); };
  const toggleBucket = (key: keyof typeof expandedBuckets) =>
    setExpandedBuckets(prev => ({ ...prev, [key]: !prev[key] }));

  const DATE_OPTS: { key: DateFilter; label: string }[] = [
    { key: 'today',      label: 'Today' },
    { key: 'yesterday',  label: 'Yesterday' },
    { key: 'last7days',  label: 'Last 7 Days' },
    { key: 'last30days', label: 'Last 30 Days' },
    { key: 'custom',     label: 'Custom' },
  ];

  // Client-side bucket correction: mirrors the backend post-classification override
  // in admin_journey_funnel_data (apis.py lines 9826-9830).
  // Old server builds may return bucket='pre_bre' for dynamic-journey pages that
  // are actually post-BRE (e.g. "Final Verification", "Identity Verification").
  // stageName is always resolved correctly from the PageConfig name, so we use it
  // as the authoritative signal when it matches a known post-BRE or BRE page name.
  const resolveBucket = (bucket: string, stageName: string): string => {
    const n = stageName.toLowerCase();
    // Post-BRE: KYC, mandate, signing pages + Caprion steps 8–9 (offer review after BRE decision)
    const POST_BRE_NAMES = [
      'final verification', 'identity verification', 'aadhaar verification',
      'agreement pdf display', 'agreement signing', 'dealer approval validation',
      // Caprion post-BRE offer steps (after AA Verification)
      'customize loan', 'final loan offer', 'final offer',
      'loan validation', 'loan detail', 'loan verification',
    ];
    // BRE2/AA: only AA-stage pages
    const BRE_NAMES = ['aa verification'];
    if (POST_BRE_NAMES.some(kw => n.includes(kw))) return 'post_bre';
    if (BRE_NAMES.some(kw => n.includes(kw))) return 'bre';
    return bucket;
  };

  const bucketBadge = (bucket: string) => {
    const meta = BUCKET_META[bucket as BucketKey];
    if (!meta) return null;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta.badgeCls}`}>
        {meta.label}
      </span>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6 w-full">

      {/* Header + Date Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Journey Funnel Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
            {usingMock ? 'Mock data (API unavailable)' : 'Live data'} · auto-refreshes every 30s
            {loading && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
            {!loading && (
              <span className="text-gray-400 text-xs">
                · last updated {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {DATE_OPTS.map(opt => (
            <Button
              key={opt.key}
              size="sm"
              variant={selectedDate === opt.key ? 'default' : 'outline'}
              className={selectedDate === opt.key ? 'bg-gray-900 text-white' : ''}
              onClick={() => { setSelectedDate(opt.key); setCurrentPage(1); }}
            >
              {opt.key === 'custom' && <Calendar className="h-3 w-3 mr-1" />}
              {opt.label}
            </Button>
          ))}
          {selectedDate === 'custom' && (
            <div className="flex items-center gap-1">
              <Input type="date" value={customDateFrom}
                onChange={e => { setCustomDateFrom(e.target.value); setCurrentPage(1); }}
                className="h-8 text-xs w-36" />
              <span className="text-gray-400 text-xs">–</span>
              <Input type="date" value={customDateTo}
                onChange={e => { setCustomDateTo(e.target.value); setCurrentPage(1); }}
                className="h-8 text-xs w-36" />
            </div>
          )}
        </div>
      </div>

      {/* ── Lender Filter Bar ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 mr-1">
          <Building2 className="h-3.5 w-3.5" />
          Lender
        </div>

        {/* All Lenders pill — always shows the full unfiltered count */}
        <button
          onClick={() => { setSelectedLender(''); setCurrentPage(1); }}
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150
            ${selectedLender === ''
              ? 'bg-gray-900 text-white border-gray-900 shadow-sm'
              : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500 hover:text-gray-900'}`}
        >
          All Lenders
          <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold
            ${selectedLender === '' ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`}>
            {allUsers.length}
          </span>
        </button>

        {/* Per-lender pills — derived from live data */}
        {lenderOptions.map(bap => {
          const label = BAP_TO_LENDER[bap] ?? bap;
          // Count from the full allUsers set (all BAP users in the date range)
          const count = allUsers.filter(u => u.bap === bap).length;
          const isActive = selectedLender === bap;
          return (
            <button
              key={bap}
              onClick={() => { setSelectedLender(bap); setCurrentPage(1); }}
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-all duration-150
                ${isActive
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400 hover:text-indigo-700'}`}
            >
              {label}
              <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold
                ${isActive ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`}>
                {count}
              </span>
            </button>
          );
        })}

        {/* Active filter indicator */}
        {selectedLender && (
          <div className="ml-auto flex items-center gap-1.5 text-xs text-indigo-700 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
            Filtered: {BAP_TO_LENDER[selectedLender] ?? selectedLender}
            <button
              onClick={() => { setSelectedLender(''); setCurrentPage(1); }}
              className="ml-1 text-indigo-400 hover:text-indigo-700 font-bold"
              title="Clear filter"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        <button
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'funnel'
              ? 'border-gray-900 text-gray-900'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('funnel')}
        >
          📊 Funnel View
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'phonepe_mis'
              ? 'border-purple-600 text-purple-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('phonepe_mis')}
        >
          📱 PhonePe MIS
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-1 ${
            activeTab === 'drop_off_alerts'
              ? 'border-red-500 text-red-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('drop_off_alerts')}
        >
          🔔 Drop-off Alerts
        </button>
      </div>

      {/* ── PhonePe MIS Tab ── */}
      {activeTab === 'phonepe_mis' && (
        <PhonePeMISTab dateFrom={misDf} dateTo={misDt} />
      )}

      {/* ── Drop-off Alerts Tab ── */}
      {activeTab === 'drop_off_alerts' && (
        <DropOffAlertsTab />
      )}

      {/* ── Funnel View Tab ── */}
      {activeTab === 'funnel' && (
        <>
          {/* Summary Cards — clicking opens a user-list modal for that bucket */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <SummaryCard title="Total Users" value={total}
              subtitle={selectedDate === 'today' ? 'Today' : selectedDate.replace(/([A-Z])/g, ' $1')}
              trend={selectedDate === 'today' ? trend : undefined}
              borderColor="border-gray-400" icon={<Users className="h-5 w-5" />}
              onClick={() => openBucketModal('all')} />
            <SummaryCard title="Pre-BRE Users" value={preBreTot}
              subtitle={total > 0 ? `${((preBreTot / total) * 100).toFixed(0)}% of total` : '—'}
              borderColor="border-blue-500" icon={<TrendingDown className="h-5 w-5 text-blue-400" />}
              onClick={() => openBucketModal('pre_bre')} />
            <SummaryCard title="BRE Users" value={breTot}
              subtitle={total > 0 ? `${((breTot / total) * 100).toFixed(0)}% of total` : '—'}
              borderColor="border-orange-500" icon={<TrendingDown className="h-5 w-5 text-orange-400" />}
              onClick={() => openBucketModal('bre')} />
            <SummaryCard title="Post-BRE Users" value={postBreTot}
              subtitle={total > 0 ? `${((postBreTot / total) * 100).toFixed(0)}% of total` : '—'}
              borderColor="border-green-500" icon={<TrendingDown className="h-5 w-5 text-green-400" />}
              onClick={() => openBucketModal('post_bre')} />
            <SummaryCard title="💰 Disbursed" value={filteredDisbursedCount}
              subtitle={filteredDisbursedAmountTotal > 0 ? fmtRupeeIN(filteredDisbursedAmountTotal) + ' total' : '—'}
              borderColor="border-emerald-500" icon={<DollarSign className="h-5 w-5 text-emerald-500" />}
              onClick={() => setIsDisbursedModalOpen(true)} />
          </div>

          {/* Funnel Bar Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-gray-800">
                User Distribution Across Journey Stages
              </CardTitle>
              <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                {(['pre_bre', 'bre', 'post_bre'] as const).map(b => (
                  <span key={b} className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: BUCKET_META[b].color }} />
                    {BUCKET_META[b].label}
                  </span>
                ))}
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: DISBURSED_COLOR }} />
                  Disbursed
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="stageName" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-40} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                  <Tooltip content={<FunnelTooltip />} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40}>
                    {chartData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Bucket Accordion Panels */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BucketPanel bucketKey="pre_bre" stages={preBre} expanded={expandedBuckets.pre_bre}
              onToggle={() => toggleBucket('pre_bre')} onSelectStage={handleSelectStage} />
            <BucketPanel bucketKey="bre" stages={bre} expanded={expandedBuckets.bre}
              onToggle={() => toggleBucket('bre')} onSelectStage={handleSelectStage} />
            <BucketPanel bucketKey="post_bre" stages={postBre} expanded={expandedBuckets.post_bre}
              onToggle={() => toggleBucket('post_bre')} onSelectStage={handleSelectStage} />
          </div>

          {/* ── Analytics Toggle Buttons ──────────────────────────────────── */}
          <div className="flex flex-wrap gap-2">
            {/* Pre-BRE Analytics Toggle */}
            <button
              onClick={() => setShowPreBREAnalytics(v => !v)}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 select-none
                ${showPreBREAnalytics
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}
            >
              📊 Pre-BRE Analytics
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${showPreBREAnalytics ? 'rotate-180' : ''}`} />
            </button>

            {/* BRE Analytics Toggle */}
            <button
              onClick={() => setShowBREAnalytics(v => !v)}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 select-none
                ${showBREAnalytics
                  ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                  : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50'}`}
            >
              📊 BRE Analytics
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${showBREAnalytics ? 'rotate-180' : ''}`} />
            </button>

            {/* Post-BRE Analytics Toggle */}
            <button
              onClick={() => setShowPostBREAnalytics(v => !v)}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 select-none
                ${showPostBREAnalytics
                  ? 'bg-green-600 text-white border-green-600 shadow-sm'
                  : 'bg-white text-green-700 border-green-300 hover:bg-green-50'}`}
            >
              📊 Post-BRE Analytics
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${showPostBREAnalytics ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* ── Pre-BRE Analytics Panel (animated slide-down) ─────────────── */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showPreBREAnalytics ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-blue-800">Pre-BRE Analytics</span>
                <Badge className="bg-blue-100 text-blue-800 text-xs">{preBreTot} users</Badge>
              </div>
              <PreBREAnalyticsPanel data={preBREAnalytics} />
            </div>
          </div>

          {/* ── BRE Analytics Panel (animated slide-down) ─────────────────── */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showBREAnalytics ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-orange-800">BRE Analytics</span>
                <Badge className="bg-orange-100 text-orange-800 text-xs">{breTot} users</Badge>
              </div>
              <BREAnalyticsPanel
                data={breAnalytics}
                pipelineUsers={pipelineUsers}
                pipelineExpanded={pipelineExpanded}
                onTogglePipeline={() => setPipelineExpanded(v => !v)}
              />
            </div>
          </div>

          {/* ── Post-BRE Analytics Panel (animated slide-down) ────────────── */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showPostBREAnalytics ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-green-800">Post-BRE Analytics</span>
                <Badge className="bg-green-100 text-green-800 text-xs">{postBreTot} users</Badge>
              </div>
              <PostBREAnalyticsPanel data={postBREAnalytics} />
            </div>
          </div>

          {/* ── Approved Pipeline Section (approved, not yet disbursed) ───── */}
          <Card className="border-t-4 border-amber-400">
            <CardHeader
              className="bg-amber-50 cursor-pointer select-none py-3 px-4"
              onClick={() => setPipelineExpanded(v => !v)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-base font-semibold text-amber-700">
                    ⏳ Approved — Pending Disbursal
                  </CardTitle>
                  <Badge className="bg-amber-100 text-amber-800 text-xs font-bold">
                    {pipelineUsers.length}
                  </Badge>
                  {pipelineTotal > 0 && (
                    <span className="text-sm font-semibold text-amber-700">
                      {fmtRupeeIN(pipelineTotal)}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">sanctioned, awaiting disbursal</span>
                </div>
                <div className="flex items-center gap-2">
                  {pipelineExpanded
                    ? <ChevronUp className="h-4 w-4 text-gray-500" />
                    : <ChevronDown className="h-4 w-4 text-gray-500" />}
                </div>
              </div>
            </CardHeader>

            {pipelineExpanded && (
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-amber-50/50">
                        <TableHead className="text-xs whitespace-nowrap">Name</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Phone</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">BAP</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Approved Amt</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Loan Amt</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Current Stage</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Last Updated</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pipelineUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-4 text-gray-400 italic">
                            No pending disbursals
                          </TableCell>
                        </TableRow>
                      ) : (
                        pipelineUsers.map((p, idx) => (
                          <TableRow key={p.user_id ?? idx} className="hover:bg-amber-50/30">
                            <TableCell className="font-medium text-sm whitespace-nowrap">
                              {p.name || '—'}
                            </TableCell>
                            <TableCell className="font-mono text-xs whitespace-nowrap">
                              {p.phone || '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {p.bap || '—'}
                            </TableCell>
                            <TableCell className="text-sm font-semibold text-amber-700 whitespace-nowrap">
                              {p.approved_loan_amount != null
                                ? fmtRupeeIN(p.approved_loan_amount)
                                : p.loan_amount != null ? fmtRupeeIN(p.loan_amount) : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {p.loan_amount != null ? fmtRupeeIN(p.loan_amount) : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap text-gray-500">
                              {p.stage_name || '—'}
                            </TableCell>
                            <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                              {p.last_updated ? toIST(p.last_updated) : '—'}
                            </TableCell>
                            {/* On-demand live CheckStatus — see PipelineCheckStatusCell. */}
                            <TableCell className="text-xs whitespace-nowrap">
                              <PipelineCheckStatusCell user={p} />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            )}
          </Card>

          {/* ── Disbursed Section ─────────────────────────────────────────── */}
          <Card className="border-t-4 border-emerald-500">
            <CardHeader
              className="bg-emerald-50 cursor-pointer select-none py-3 px-4"
              onClick={() => setDisbursedExpanded(v => !v)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-base font-semibold text-emerald-700">
                    💰 Disbursed
                  </CardTitle>
                  <Badge className="bg-emerald-100 text-emerald-800 text-xs font-bold">
                    {filteredDisbursedCount}
                  </Badge>
                  {filteredDisbursedAmountTotal > 0 && (
                    <span className="text-sm font-semibold text-emerald-700">
                      {fmtRupeeIN(filteredDisbursedAmountTotal)}
                    </span>
                  )}
                  {completedCount > 0 && (
                    <span className="text-xs text-gray-500">
                      {filteredDisbursedCount > 0
                        ? `${((filteredDisbursedCount / completedCount) * 100).toFixed(1)}% of Completed`
                        : 'No disbursals in range'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {completedCount > 0 && filteredDisbursedCount < completedCount && (
                    <span className="flex items-center gap-1 text-xs font-medium text-red-500">
                      <TrendingDown className="h-3 w-3" />
                      {(((completedCount - filteredDisbursedCount) / completedCount) * 100).toFixed(1)}% drop-off from Completed
                    </span>
                  )}
                  {disbursedExpanded
                    ? <ChevronUp className="h-4 w-4 text-gray-500" />
                    : <ChevronDown className="h-4 w-4 text-gray-500" />}
                </div>
              </div>
            </CardHeader>

            {disbursedExpanded && (
              <CardContent className="p-0">
                {filteredDisbursedUsers.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-400">
                    No disbursals found for the selected date range{selectedLender ? ` and lender filter` : ''}.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-emerald-50/50">
                          <TableHead className="text-xs whitespace-nowrap">Loan ID</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Name</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Phone</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Lender</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Disbursed Amt</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Loan Amt</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Amt in Bank</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Daily EDI</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Total Fee</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Repay Total</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">ROI</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Tenure</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">EMI/EDI</TableHead>
                          <TableHead className="text-xs whitespace-nowrap">Date of Disbursal</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredDisbursedUsers.map((d, idx) => (
                          <TableRow key={d.intellend_loan_id || idx} className="hover:bg-emerald-50/30">
                            <TableCell className="font-mono text-xs text-emerald-700 whitespace-nowrap">
                              {d.intellend_loan_id || '—'}
                            </TableCell>
                            <TableCell className="font-medium text-sm whitespace-nowrap">
                              {disbursedDisplayName(d) ?? '—'}
                            </TableCell>
                            <TableCell className="font-mono text-xs whitespace-nowrap">
                              {disbursedDisplayPhone(d) ?? '—'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700">
                                {d.lender_code || '—'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm font-semibold text-emerald-700 whitespace-nowrap">
                              {d.disbursed_amount != null ? fmtRupeeIN(d.disbursed_amount) : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {d.loan_amount != null ? fmtRupeeIN(d.loan_amount) : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {d.offer_loan_amt_net != null ? fmtRupeeIN(d.offer_loan_amt_net) : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {d.offer_daily_repayment != null ? fmtRupeeIN(d.offer_daily_repayment) : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {d.offer_total_fee != null ? fmtRupeeIN(d.offer_total_fee) : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {d.offer_repay_total != null ? fmtRupeeIN(d.offer_repay_total) : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {d.roi != null ? `${d.roi}%` : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {d.tenure != null ? `${d.tenure}m` : '—'}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {d.epi != null ? fmtRupeeIN(d.epi) : '—'}
                            </TableCell>
                            <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                              {d.date_of_disbursal
                                ? toIST(d.date_of_disbursal)
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* ── Disbursed Analytics Toggle + Panel ────────────────────────── */}
          <div className="flex">
            <button
              onClick={() => setShowDisbAnalytics(v => !v)}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 select-none
                ${showDisbAnalytics
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                  : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'}`}
            >
              📊 Disbursed Analytics
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${showDisbAnalytics ? 'rotate-180' : ''}`} />
            </button>
          </div>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showDisbAnalytics ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-emerald-800">Disbursed Analytics</span>
                <Badge className="bg-emerald-100 text-emerald-800 text-xs">{filteredDisbursedCount} disbursals</Badge>
              </div>
              <DisbursedAnalyticsPanel data={disbursedAnalytics} />
            </div>
          </div>

          {/* Latest Journeys Table */}
          <Card>
            <CardHeader className="pb-3">
              {/* ── Row 1: title + action buttons ──────────────────────────── */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  Latest Journeys
                  <Badge variant="outline" className="text-xs font-normal">{tableUsers.length} user{tableUsers.length !== 1 ? 's' : ''}</Badge>
                  {activeFilterCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                      {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
                    </span>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="gap-1 text-xs"
                    onClick={() => downloadCSV(tableUsers, 'journey_funnel_all.csv')}>
                    <Download className="h-3 w-3" />Download All
                  </Button>
                  <Button size="sm" variant="ghost" title="Refresh now"
                    onClick={() => { setLoading(true); loadData().finally(() => setLoading(false)); }}
                    disabled={loading}>
                    {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  </Button>
                </div>
              </div>

              {/* ── Row 2: Search + filter dropdowns (BRE Analysis style) ──── */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] gap-2 pt-2 border-t border-gray-100 mt-1 items-end">
                {/* Search */}
                <div>
                  <p className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wide">Search</p>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                      placeholder="Phone, name, application ID, business, stage…"
                      value={searchQuery}
                      onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      className="pl-8 h-9 text-sm w-full"
                    />
                  </div>
                </div>

                {/* Final status */}
                <div>
                  <p className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wide">Final status</p>
                  <select
                    value={filterFinalStage}
                    onChange={e => { setFilterFinalStage(e.target.value); setCurrentPage(1); }}
                    className="h-9 text-sm border border-gray-200 rounded-md px-3 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 min-w-[140px]"
                  >
                    <option value="">All statuses</option>
                    <option value="APPROVED">APPROVED</option>
                    <option value="REJECTED">REJECTED</option>
                    <option value="PENDING">PENDING</option>
                    <option value="CREATED">CREATED</option>
                    <option value="OPS_VERIFICATION">OPS_VERIFICATION</option>
                    <option value="WITHDRAWN">WITHDRAWN</option>
                  </select>
                </div>

                {/* Source slug */}
                <div>
                  <p className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wide">Source slug</p>
                  <select
                    value={filterSourceSlug}
                    onChange={e => { setFilterSourceSlug(e.target.value); setCurrentPage(1); }}
                    className="h-9 text-sm border border-gray-200 rounded-md px-3 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 min-w-[140px]"
                  >
                    <option value="">All sources</option>
                    {sourceSlugOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                {/* Current page (stage) */}
                <div>
                  <p className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wide">Current page</p>
                  <select
                    value={filterStage}
                    onChange={e => { setFilterStage(e.target.value); setCurrentPage(1); }}
                    className="h-9 text-sm border border-gray-200 rounded-md px-3 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 min-w-[160px]"
                  >
                    <option value="">All pages</option>
                    {stageOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                {/* Bucket */}
                <div>
                  <p className="text-[10px] text-gray-400 mb-1 font-medium uppercase tracking-wide">Bucket</p>
                  <select
                    value={filterBucket}
                    onChange={e => { setFilterBucket(e.target.value); setCurrentPage(1); }}
                    className="h-9 text-sm border border-gray-200 rounded-md px-3 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 min-w-[120px]"
                  >
                    <option value="">All buckets</option>
                    <option value="pre_bre">Pre-BRE</option>
                    <option value="bre">BRE</option>
                    <option value="post_bre">Post-BRE</option>
                  </select>
                </div>
              </div>

              {/* Active filter clear row + per-page selector */}
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                <span className="text-xs text-gray-500">
                  {tableUsers.length > 0
                    ? `Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, tableUsers.length)} of ${tableUsers.length} record${tableUsers.length !== 1 ? 's' : ''}`
                    : '0 records'}
                </span>
                <div className="flex items-center gap-1 ml-2">
                  <span className="text-xs text-gray-400">Per page:</span>
                  <select
                    value={pageSize}
                    onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300"
                  >
                    {[25, 50, 100, 200, 500].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearTableFilters}
                    className="ml-auto text-xs text-red-500 hover:text-red-700 font-medium"
                  >
                    ✕ Clear all filters
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                        <TableHead className="w-16 text-xs">ID</TableHead>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Business</TableHead>
                        <TableHead className="text-xs">Phone</TableHead>
                        <TableHead className="text-xs">Stage</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Approved Amt</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Loan Amount</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Amt in Bank</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Daily EDI</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Total Fee</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Repay Total</TableHead>
                        <TableHead className="text-xs">Bucket</TableHead>
                        <TableHead className="text-xs">Final Stage</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Source Slug</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Application ID</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Order ID / URN</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">BRE1 Response</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">BRE2 Response</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Lead Created (IST)</TableHead>
                        <TableHead
                          className="text-xs cursor-pointer select-none hover:text-gray-900"
                          onClick={() => { setTableSortAsc(v => !v); setCurrentPage(1); }}
                        >
                          Last Updated {tableSortAsc ? '↑' : '↓'}
                        </TableHead>
                        <TableHead className="text-xs w-20">Action</TableHead>
                      </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={21} className="text-center text-gray-400 py-8">
                          No users found for the selected date range.
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedUsers.map(u => (
                        <TableRow key={u.id} className="hover:bg-gray-50">
                          <TableCell className="font-mono text-xs text-gray-500">{u.id}</TableCell>
                          <TableCell className="font-medium text-sm">{u.name}</TableCell>
                          <TableCell className="text-sm text-gray-600 truncate max-w-[140px]" title={u.businessName || ''}>{u.businessName || '—'}</TableCell>
                          <TableCell className="font-mono text-sm">{u.phone}</TableCell>
                          <TableCell>
                            <span className="text-xs font-medium text-gray-700">{u.stageName}</span>
                          </TableCell>
                          <TableCell className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {u.approved_loan_amount != null
                              ? `₹${Number(u.approved_loan_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {u.loan_amount != null && u.loan_amount > 0
                              ? `₹${u.loan_amount.toLocaleString('en-IN')}`
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {u.offer_loan_amt_net != null
                              ? `₹${Number(u.offer_loan_amt_net).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {u.offer_daily_repayment != null
                              ? `₹${Number(u.offer_daily_repayment).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {u.offer_total_fee != null
                              ? `₹${Number(u.offer_total_fee).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                              : '—'}
                          </TableCell>
                          <TableCell className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {u.offer_repay_total != null
                              ? `₹${Number(u.offer_repay_total).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                              : '—'}
                          </TableCell>
                          <TableCell>{bucketBadge(resolveBucket(u.bucket, u.stageName))}</TableCell>
                          <TableCell>
                            <FinalStatusBadge
                              status={u.caprion_final_status}
                              onClick={u.caprion_check_status_id != null ? () => { setJsonViewerUser(u); setJsonViewerId(u.caprion_check_status_id ?? null); setIsJsonViewerOpen(true); } : undefined}
                            />
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 whitespace-nowrap font-mono">
                            {u.source_slug ?? '—'}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 whitespace-nowrap font-mono">
                            {u.application_id ?? '—'}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 whitespace-nowrap font-mono">
                            {u.order_id ?? '—'}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {u.bre1_status != null ? (
                              <span className="inline-flex items-center gap-1">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                                  u.bre1_status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                  u.bre1_status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                  u.bre1_status === 'OPS_VERIFICATION' ? 'bg-blue-100 text-blue-800' :
                                  u.bre1_status === 'WITHDRAWN' ? 'bg-gray-200 text-gray-700' :
                                  'bg-amber-100 text-amber-800'
                                }`}>
                                  {u.bre1_status}
                                </span>
                                {/*
                                 * Rendered whenever a BRE1 status exists —
                                 * deliberately NOT gated on the id. The body is
                                 * no longer embedded in this response, so this
                                 * link is the ONLY way to reach it; gating it on
                                 * an id meant a row with a missing id silently
                                 * lost the feature. The modal reports the reason
                                 * if the reference cannot be resolved.
                                 */}
                                <button
                                  className="text-xs text-blue-500 hover:underline"
                                  onClick={() => { setJsonViewerUser(u); setJsonViewerId(u.bre1_response_id ?? u.caprion_check_status_id ?? null); setIsJsonViewerOpen(true); }}
                                  title="Click to load the BRE1 response"
                                >JSON</button>
                              </span>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {u.bre2_status != null ? (
                              <span className="inline-flex items-center gap-1">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                                  u.bre2_status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                  u.bre2_status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                  u.bre2_status === 'OPS_VERIFICATION' ? 'bg-blue-100 text-blue-800' :
                                  u.bre2_status === 'WITHDRAWN' ? 'bg-gray-200 text-gray-700' :
                                  'bg-amber-100 text-amber-800'
                                }`}>
                                  {u.bre2_status}
                                </span>
                                <button
                                  className="text-xs text-blue-500 hover:underline"
                                  onClick={() => { setJsonViewerUser(u); setJsonViewerId(u.bre2_response_id ?? u.caprion_check_status_id ?? null); setIsJsonViewerOpen(true); }}
                                  title="Click to load the BRE2 response"
                                >JSON</button>
                              </span>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                            {toIST(u.userCreatedAt)}
                          </TableCell>
                          <TableCell className="text-xs text-gray-500">
                            {toIST(u.lastUpdated)}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={() => { const sc = stageCounts.find(s => s.stage === u.stage); if (sc) handleSelectStage(sc); }}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 flex-wrap gap-2">
                  <p className="text-xs text-gray-500">
                    Page {currentPage} of {totalPages} · {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, tableUsers.length)} of {tableUsers.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 px-2"
                      disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                      ‹ Prev
                    </Button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i;
                      return (
                        <Button key={page} size="sm"
                          variant={page === currentPage ? 'default' : 'outline'}
                          className={`h-7 w-7 p-0 text-xs ${page === currentPage ? 'bg-gray-900 text-white' : ''}`}
                          onClick={() => setCurrentPage(page)}>
                          {page}
                        </Button>
                      );
                    })}
                    <Button size="sm" variant="outline" className="h-7 px-2"
                      disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
                      Next ›
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Disbursed Users Modal */}
      <DisbursedModal
        users={filteredDisbursedUsers}
        open={isDisbursedModalOpen}
        onClose={() => setIsDisbursedModalOpen(false)}
      />

      {/* Stage User Modal */}
      <StageModal
        sc={selectedStage}
        open={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedStage(null); }}
        onViewJson={(u) => { setJsonViewerUser(u); setJsonViewerId(u.caprion_check_status_id ?? null); setIsJsonViewerOpen(true); }}
      />

      {/* Caprion JSON Viewer Modal */}
      <CaprionJSONViewerModal
        user={jsonViewerUser}
        responseId={jsonViewerId}
        open={isJsonViewerOpen}
        onClose={() => { setIsJsonViewerOpen(false); setJsonViewerUser(null); setJsonViewerId(null); }}
      />

      {/* BRE Reject Detail Modal */}
      <BRERejectModal
        user={breModalUser}
        open={isBreModalOpen}
        onClose={() => { setIsBreModalOpen(false); setBreModalUser(null); }}
      />
    </div>
  );
};

export default FunnelDashboard;